/**
 * CRMObjectListPage — Full list page using the new design system.
 *
 * Composes the page header, filter chips, the shared Table primitive, and
 * BulkActionBar. Adds:
 *  - Unsaved-changes guard when an inline edit is in progress
 *  - Keyboard navigation (↑/↓) for the open slideout
 *  - Touch-device detection for adapted interactions
 *  - Responsive column collapsing on narrow viewports
 */

import { useQueryClient } from '@tanstack/react-query';
import { Edit03, GitMerge, LayoutGrid01, Users01 } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useCrmExport } from '@/hooks/useCrmExport';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import {
  useCanWrite,
  useFeatureAccess,
  usePermissions,
} from '@/hooks/usePermissions';
import { useTitle } from '@/hooks/useTitle';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { Button } from '@/lib/base/buttons/button';
import { FormProvider } from '@/lib/base/forms/form-context';
import { SubmitButton } from '@/lib/base/forms/submit-button';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import { UnsavedChangesModal } from '@/lib/base/overlays/unsaved-changes-modal';
import type { HeaderAction } from '@/lib/base/page-layout/page-layout';
import type {
  DataTableColumnConfig,
  DataTableRowData,
  FooterRenderContext,
} from '@/lib/base/table';
import { DataTable } from '@/lib/base/table';
import type { AddColumnOption } from '@/lib/base/table/components/add-column-header';
import type { RowContextMenuAction } from '@/lib/base/table/components/row-context-menu';
import { canMergeEntityTypes } from '@/lib/crm/components/merge/mergeEligibility';
import { MergeWizard } from '@/lib/crm/components/merge/MergeWizard';
import SchemaFilterBuilder, {
  hasIncompleteFilterConditions,
} from '@/lib/crm/components/SchemaFilterBuilder';
import { buildGenericCreateConfig, useCreateSlideout } from '@/lib/crm/create';
import type { FilterCondition } from '@/lib/crm/filters/types';
import type { FilterConditionGroup } from '@/lib/crm/filters/types';
import { useListDefaultView } from '@/lib/crm/hooks/useListDefaultView';
import {
  type SavedViewSurface,
  useSavedViews,
} from '@/lib/crm/hooks/useSavedViews';
import { EXCLUDED_LINK_TARGETS } from '@/lib/crm/record/memo/linkPickerFilters';
import { isCreatableEntityKind } from '@/lib/crm/utils/creatableKinds';
import { isApiError } from '@/lib/errors';
import {
  clearActiveBulkActionJob,
  getActiveBulkActionJob,
  setActiveBulkActionJob,
} from '@/lib/localStorage/bulkActionActiveJob';
import { logger } from '@/lib/logger';
import type { CrmObject, FilterGroup } from '@/types/crm';
import {
  getPropertyValue,
  isAsyncSingleObjectDeleteResponse,
} from '@/types/crm';
import type {
  SavedFilterView,
  SavedFilterViewState,
} from '@/types/savedFilterView';
import {
  andBaselineIntoGroups,
  convertConditionGroupsToFilterGroups,
  filterGroupsToConditionGroups,
} from '@/utils/filterUtils';

import { BulkUpdateModal } from '../../components/bulk-actions';
import { DeleteCRMObjectModal } from '../../components/DeleteCRMObjectModal';
import { BULK_DELETE_ASYNC_THRESHOLD } from '../../core/constants';
import type { BulkActionSelection } from '../../core/CRMObjectClient';
import { createCRMObjectClient } from '../../core/CRMObjectClient';
import { translateBulkDeleteError } from '../../core/errors';
import type { ListPageConfig } from '../../core/types';
import type { BulkActionRequirement } from '../../core/types';
import type { ColumnConfig } from '../../core/types';
import { useActiveListView } from '../../hooks/useActiveListView';
import { useAllPropertySchemasByType } from '../../hooks/useAllPropertySchemasByType';
import { useAssociationMatchers } from '../../hooks/useAssociationMatchers';
import { useAssociationTypes } from '../../hooks/useAssociationTypes';
import { useFieldTypes } from '../../hooks/useFieldTypes';
import { useObjectTypes } from '../../hooks/useObjectTypes';
import type { PropertySchemaMap } from '../../hooks/usePropertySchema';
import { usePropertySchema } from '../../hooks/usePropertySchema';
import { useSlideoutStack } from '../../hooks/useSlideoutStack';
import { useUpdateCrmAssociation } from '../../hooks/useUpdateCrmAssociation';
import { useUpdateCrmProperty } from '../../hooks/useUpdateCrmProperty';
import { useListState } from '../../list/hooks/useListState';
import { useListUrlSync } from '../../list/hooks/useListUrlSync';
import {
  decodeFiltersParam,
  FILTERS_PARAM,
  MODE_PARAM,
  readInitialListUrlState,
} from '../../list/urlListParams';
import { getColumnKey, getColumnLabel } from '../../list/utils/getColumnLabel';
import { renderColumnCell } from '../../list/utils/renderColumnCell';
import { isCrmListKey } from '../../query/queries';
import { getEntityTypeConfig } from '../../record/registry/entityTypeRegistry';
import { DISPLAY_NAME_FIELDS } from '../../record/registry/fieldConstants';
import {
  clearAll as clearCrmObjectStore,
  getObject as getStoreObject,
} from '../../store/crmObjectStore';
import { useCrmObjectListInfinite } from '../../store/hooks';
import {
  currentSingleObjectDeleteJobGeneration,
  startSingleObjectDeleteJob,
} from '../../store/singleObjectDeleteJobStore';
import { submitEventsOptimistic } from '../../store/submitEventsOptimistic';
import { isAssociationValueFrozen } from '../../utils/associationEditability';
import { getEntityTypeIconComponent } from '../../utils/icons/EntityTypeIcon';
import { getPropertyTypeIconComponent } from '../../utils/icons/PropertyTypeIcon';
import { isPropertyEditable } from '../../utils/propertyEditability';
import { isUiCreatableEntityType } from '../../utils/uiCreatableTargets';
import { buildExportColumns } from '../utils/buildExportColumns';
import {
  deriveVisibleColumnAssociations,
  deriveVisibleColumnProperties,
} from '../utils/deriveVisibleColumnConfig';
import type { LookupColumnOption } from '../utils/lookupColumn';
import {
  buildLookupColumnOptions,
  isLookupColumnSortable,
  isLookupColumnTargetResolved,
  parseLookupColumnName,
  reconstructLookupColumnFromSavedId,
} from '../utils/lookupColumn';
import { reconcileColumnOrder } from '../utils/reconcileColumnOrder';
import { BulkActionBar } from './bulk-action-bar';
import { BulkActionBanner } from './BulkActionBanner';
import { CrmListLayout } from './crm-list-layout';
import { FilterConditionChip } from './FilterChip';
import { getFilterConditionDisplayLabel } from './filterChipLabels';
import { isFilterConditionValid } from './filterConditionDisplay';
import { SaveCurrentViewModal } from './SaveCurrentViewModal';
import { SavedViewTabs, type SavedViewTabsProps } from './SavedViewTabs';
import { TotalsRow } from './TotalsRow';

// ---------------------------------------------------------------------------
// Module-scope constants (stable references — not inside component)
// ---------------------------------------------------------------------------

// Associations that are real (readable + writable) but are managed by dedicated
// UI elsewhere and carry no value as a list column. They were cluttering the
// "+" add-column dropdown. Owner is intentionally NOT here — it's a meaningful
// assignee column. Target-type-based junk (import / file / team / note pipeline
// / stage timer / *_timeline) is filtered separately via EXCLUDED_LINK_TARGETS.
const SYSTEM_PICKER_ASSOCIATION_TYPES = new Set<string>([
  'ahoy_note_to_creator',
  'ahoy_note_to_mentioned_user',
]);

const LIST_ERROR_HEADING = 'We couldn’t load this list';
const LIST_ERROR_BODY_FILTERED =
  'Try again, or simplify the filters on this view.';
const LIST_ERROR_BODY_UNFILTERED = 'Try again in a moment.';

// ---------------------------------------------------------------------------
// Props — same contract as CRMObjectListPage
// ---------------------------------------------------------------------------

export interface CRMObjectListPageProps {
  config: ListPageConfig;
  /**
   * Callback to create an object. When omitted, the Add button and empty-state
   * "create first" action are hidden — use this for system-created entity types
   * that cannot be created manually (e.g., email threads).
   */
  onCreateObject?: (
    orgId: number,
    data: Record<string, unknown>
  ) => Promise<void>;
  /**
   * Optional callback forwarded to the default create slideout's form as
   * `onPropertyChange`. Fires on every create-form field change. Use it to react
   * to field entry before submit — e.g. the contacts page debounces this on
   * `ahoy_email` to prefetch the dedup lookup so it's off the submit path.
   */
  onCreatePropertyChange?: (propertyTypeName: string, value: unknown) => void;
  /**
   * Optional override for the Add button click handler. When provided,
   * the Add button is shown and clicking it invokes this callback instead
   * of opening the default create slideout. Use this to wire a custom
   * create flow (e.g., a bespoke `CrmCreateConfig`). Both `onCreateObject` and
   * `onCreateClick` cause the Add button to render.
   */
  onCreateClick?: () => void;
  /**
   * When true, the create affordances wired by `onCreateClick` (the Add button
   * and the empty-state "create first" CTA) render disabled rather than active.
   * Use for a create flow that is momentarily unavailable — e.g. a record's
   * Tasks tab while the default task stage is still resolving — so the control
   * stays visible but can't be clicked into a silent no-op.
   */
  createDisabled?: boolean;
  /**
   * Tooltip shown on the Add button when it is disabled *because of*
   * `createDisabled` (i.e. the user HAS create permission but the flow is
   * momentarily unavailable). Names the transient reason — e.g. "Loading…"
   * while a record's default task stage resolves. Omit for no tooltip. A
   * missing per-entity create permission always overrides this with its own
   * permission message, so this never masks a real permission block.
   */
  createDisabledTooltip?: string;
  onViewObject?: (object: CrmObject) => void;
  enableDelete?: boolean;
  /** Content rendered before search/filter in toolbar */
  toolbarLeading?: React.ReactNode;
  /** Content pinned to the far right of the toolbar row */
  toolbarTrailing?: React.ReactNode;
  /**
   * Extra header actions inserted before the Add button (and after
   * Import/Export when visible). Use for page-specific shortcuts like
   * "View Bookings" on the Booking Links page.
   */
  extraHeaderActions?: HeaderAction[];
  /** Override the count label in the subtitle (e.g., 'threads for Kevin') */
  countLabel?: string;
  /** Singular form of the count label (e.g., 'thread for Kevin') */
  countLabelSingular?: string;
  /**
   * Embedded mode — drops the page title row + outer padding so the list can
   * be hosted inside another surface (e.g. a record-page tab). Passed through
   * to CrmListLayout/PageLayout.
   */
  embedded?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Entity types whose list rows get a hover "open record" arrow that navigates
// to the full record page, mapped to their full-page route slug
// (`/:orgId/crm/<slug>/:recordId`). Scoped to the core CRM objects for now —
// other types (meetings, notes, email threads, custom entities) still open via
// the slideout only.
const FULL_PAGE_ROUTE_SLUGS: Record<string, string> = {
  ahoy_contact: 'contacts',
  ahoy_company: 'companies',
  ahoy_deal: 'deals',
};

/** Whether an association's target entity type is a stage-kind entity. */
function isStageTargetKind(
  targetEntityTypeName: string,
  entityKindsMap: Map<string, readonly string[]>
): boolean {
  const targetKinds = entityKindsMap.get(targetEntityTypeName) ?? [];
  return targetKinds.includes('stage');
}

// Declared `requires` for page-level bulk actions (ContactsList.tsx et al.)
// that don't yet set it on their own BulkAction definition. Keyed by action
// id — remove an entry once its owning page declares `requires` directly.
const KNOWN_BULK_ACTION_REQUIREMENTS: Record<string, BulkActionRequirement> = {
  'create-deal': { permission: ['ahoy_deal', 'ahoy_object_created'] },
  // Enrich creates an ahoy_enrichment_request entity per contact, not a
  // property write on the contact — see BulkActionViewSet.bulk_enrich's
  // permission check ("ahoy_object_created on ahoy_enrichment_request").
  'enrich-auto': {
    permission: ['ahoy_enrichment_request', 'ahoy_object_created'],
  },
};

/** Whether the current user's grants satisfy a declared bulk-action requirement. */
function isBulkActionRequirementMet(
  requires: BulkActionRequirement,
  ctx: {
    hasFeature: ReturnType<typeof useFeatureAccess>['hasFeature'];
    hasPermission: ReturnType<typeof usePermissions>['hasPermission'];
  }
): boolean {
  if ('feature' in requires) {
    return ctx.hasFeature(requires.feature);
  }
  const [entityType, eventType] = requires.permission;
  return ctx.hasPermission(entityType, eventType);
}

/** Sort columns by a stored order. Unrecognised columns sort to the end. */
function sortColumnsByOrder<T>(
  cols: T[],
  order: string[] | null,
  getKey: (col: T) => string
): T[] {
  if (!order) {
    return cols;
  }
  const orderMap = new Map(order.map((id, i) => [id, i]));
  return [...cols].sort((a, b) => {
    const aIdx = orderMap.get(getKey(a)) ?? Infinity;
    const bIdx = orderMap.get(getKey(b)) ?? Infinity;
    return aIdx - bIdx;
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CRMObjectListPage({
  config,
  onCreateObject,
  onCreatePropertyChange,
  onCreateClick,
  createDisabled = false,
  createDisabledTooltip,
  onViewObject,
  enableDelete = true,
  toolbarLeading,
  toolbarTrailing,
  extraHeaderActions,
  countLabel,
  countLabelSingular,
  embedded = false,
}: CRMObjectListPageProps) {
  // Skip the global page title when embedded — the host (e.g. a record page)
  // owns the title in that context, and overwriting it would clobber the
  // parent's record name.
  useTitle(embedded ? '' : config.pluralTitle || config.title);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthInfo();
  const { currentOrg } = useOrg();
  const queryClient = useQueryClient();
  const { open: openCreateSlideout } = useCreateSlideout();
  // featureFlags consumed for future feature-gating (e.g., read_only_crm, column config)
  const { data: _featureFlags = [] } = useFeatureFlags(currentOrg?.id);
  const { hasPermission } = usePermissions();
  const { hasFeature } = useFeatureAccess();
  // Canonical residual-affordance write gate (matches the dashboards/imports/
  // API-keys treatment): a viewer (no 'ahoy_default' group) never sees a
  // write affordance at all — not even disabled. Members who DO have
  // 'ahoy_default' but lack the specific per-entity create permission still
  // get the existing disabled-with-tooltip treatment below (canCreateObject).
  const canWrite = useCanWrite();

  // `objectTypesFetched` (React Query `isFetched`) is threaded into lookup
  // degradation below so a stale/empty `initialData` array still being
  // refetched never false-drops a valid saved kind-based lookup column.
  const { data: objectTypesData, isFetched: objectTypesFetched } =
    useObjectTypes(currentOrg?.id);
  // entity_type_name -> label, for the 'type' column on kind-based lists that
  // span multiple entity types.
  const entityTypeLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const t of objectTypesData?.allObjectTypes ?? []) {
      map[t.entity_type_name] = t.label;
    }
    return map;
  }, [objectTypesData?.allObjectTypes]);
  // Per-user permission gate — applies to EVERY create path, including a
  // caller-supplied onCreateClick handler. When it fails, the Add button
  // renders disabled with a permission tooltip (not hidden).
  const canCreateObject = hasPermission(
    config.entityType,
    'ahoy_object_created'
  );
  // Two additional gates that apply only to the GENERIC create-modal path —
  // a caller-supplied onCreateClick is a bespoke flow (notes composer,
  // meeting-type wizard) and is not second-guessed here. When these fail the
  // generic Add affordance is HIDDEN (a permission tooltip would mislead):
  // (1) the schema-level user_creatable flag — system-managed types (users,
  // email threads…) resolve ahoy_object_created to internal-only, no user
  // principal may create them whatever permissions they hold; missing field
  // (cached pre-rollout schema) is treated as creatable, the backend
  // enforces — and (2) the UI-create blocklist: stage / user targets are
  // created elsewhere (pipeline config, invitations), never from a generic
  // "Add" — a separate axis from the backend user_creatable flag.
  const typeRecord = objectTypesData?.allObjectTypes?.find(
    t => t.entity_type_name === config.entityType
  );
  const typeUserCreatable = typeRecord?.user_creatable !== false;
  const genericTypeCreateAllowed =
    typeUserCreatable &&
    isUiCreatableEntityType(config.entityType, typeRecord?.entity_kind ?? []);
  const canDeleteObject = hasPermission(
    config.entityType,
    'ahoy_object_deleted'
  );
  // "Create new property" needs BOTH the user's permission to create property
  // types AND a type that accepts custom properties — system types
  // (automations, users, …) declare allows_custom_properties: false and the
  // backend rejects the write, so the affordance would only ever fail. A
  // missing flag (cached pre-rollout schema) is treated as allowed; the
  // backend enforces.
  const canCreateAttribute =
    hasPermission('ahoy_property_type', 'ahoy_object_created') &&
    typeRecord?.allows_custom_properties !== false;
  const canMergeObject = hasPermission(config.entityType, 'ahoy_object_merged');
  // Gate for inline cell editing — mirrors the record overview's edit-mode
  // gate (overview-section.tsx): the entity type must not be read-only in the
  // registry, and the user needs the property-update permission.
  const canUpdateProperties =
    !getEntityTypeConfig(config.entityType).readOnly &&
    hasPermission(config.entityType, 'ahoy_property_updated');
  // Gate for inline association editing. Replacing a target set both adds
  // and removes edges, so require both event permissions.
  const canUpdateAssociations =
    !getEntityTypeConfig(config.entityType).readOnly &&
    hasPermission(config.entityType, 'ahoy_association_added') &&
    hasPermission(config.entityType, 'ahoy_association_removed');

  // Mount-time snapshot of the URL-synced list params (?filters=/?sort=/
  // ?search=), frozen in a lazy useState so remounts re-read the CURRENT URL
  // but re-renders never re-seed. Kept current for imperative readers via
  // searchParamsRef below.
  const [initialUrlState] = useState(() =>
    readInitialListUrlState(searchParams)
  );
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  // Stable reader of the CURRENT ?filters= overrides — used by useSavedViews
  // to decide whether an apply must preserve the surface's filters.
  const getUrlFilterOverrides = useCallback(
    () => decodeFiltersParam(searchParamsRef.current.get(FILTERS_PARAM)),
    []
  );

  // List state. `defaultFilterConditions` (when present on the config) seeds
  // the visible filter chips so users can see and clear the page's defaults
  // — distinct from `defaultFilters.filter_groups` which models genuinely
  // forced/hidden filters. `initialUrlState` (deep link) overrides both.
  const listState = useListState(config.defaultFilters, 'table', {
    defaultFilterConditions: config.defaultFilterConditions,
    initialUrlState,
  });

  // Per-user default view preference — feeds useActiveListView's one-shot
  // defaultView option below.
  const {
    defaultViewRef,
    isDefaultResolving,
    setDefaultView: handleSetDefaultView,
    clearDefaultView: handleClearDefaultView,
  } = useListDefaultView(currentOrg?.id, config.entityType);

  // Must precede filtersWithSearch: builtInBaselineGroups feeds it. Owns the
  // single active-view identity (built-in OR saved) and its URL param; the
  // controller is handed to useSavedViews below so a saved-view apply commits
  // through the same identity.
  const activeListView = useActiveListView(config.builtInViews, {
    defaultView: defaultViewRef,
  });
  const { activeView, builtInBaselineGroups, setActiveView } = activeListView;

  // Schema — the representative type's; widened to the whole kind below.
  const { schema: representativeSchemas, loading: schemaLoading } =
    usePropertySchema(currentOrg?.id || 0, config.entityType);
  // Use kind-aware matchers — entity types like ahoy_contact match
  // associations defined on entity_kind=person (e.g. ahoy_contact_to_note),
  // which a `source_entity_type_name === ...` check would silently miss.
  const { associationTypes, matchesSource, associationTypesFetched } =
    useAssociationMatchers(currentOrg?.id || 0);
  const { associationLabelsByName } = useAssociationTypes(currentOrg?.id || 0);
  // Target schemas for lookup columns — keyed by target entity type name,
  // then by property name. Fetched once and threaded into label/cell
  // rendering and the degraded-lookup-column check below.
  const { schemasByType: lookupTargetSchemasByType } =
    useAllPropertySchemasByType(currentOrg?.id);
  // A kind-based list spans several entity types (an automations list shows
  // event AND scheduled automations), so its columns must be resolvable from
  // every type in the kind — otherwise a property only a sibling type carries
  // (a scheduled automation's Schedule) can never be offered or rendered.
  // Merge the siblings' schemas under the representative type's, which wins
  // on any name they share, so its labels/access rules stay authoritative.
  const propertySchemas = useMemo<PropertySchemaMap>(() => {
    if (!config.entityKind) {
      return representativeSchemas;
    }
    const merged: PropertySchemaMap = {};
    for (const type of objectTypesData?.allObjectTypes ?? []) {
      if (
        type.entity_type_name !== config.entityType &&
        type.entity_kind.includes(config.entityKind)
      ) {
        Object.assign(merged, lookupTargetSchemasByType[type.entity_type_name]);
      }
    }
    return { ...merged, ...representativeSchemas };
  }, [
    config.entityKind,
    config.entityType,
    representativeSchemas,
    objectTypesData?.allObjectTypes,
    lookupTargetSchemasByType,
  ]);
  const { data: fieldTypes } = useFieldTypes();
  // Whether a field type is scalar-sortable, per the backend's per-field-type
  // `sortable` flag. Shared by property-column and lookup-column sort gating so
  // the UI never offers a sort the backend rejects.
  const isFieldTypeSortable = useCallback(
    (fieldTypeName: string): boolean =>
      fieldTypes?.find(ft => ft.field_type_name === fieldTypeName)?.sortable ??
      false,
    [fieldTypes]
  );
  const entityKindsMap = useMemo(() => {
    const map = new Map<string, readonly string[]>();
    for (const t of objectTypesData?.allObjectTypes ?? []) {
      map.set(t.entity_type_name, t.entity_kind);
    }
    return map;
  }, [objectTypesData?.allObjectTypes]);
  const genericCreateAllowed = isCreatableEntityKind(
    config.entityType,
    entityKindsMap
  );

  // Searchable property names
  const searchableProperties = useMemo(
    () =>
      config.columns
        .filter(col => col.kind === 'property' && col.name)
        .map(col => col.name ?? ''),
    [config.columns]
  );

  // Filters with search properties injected and the built-in view's baseline
  // ANDed in. Every downstream consumer of filtersWithSearch (export, bulk
  // delete/update, TotalsRow, totalCount) inherits the baseline for free.
  const filtersWithSearch = useMemo(() => {
    const base = { ...listState.filters };
    const mergedGroups = andBaselineIntoGroups(
      base.filter_groups,
      builtInBaselineGroups
    );
    if (mergedGroups.length > 0) {
      base.filter_groups = mergedGroups;
    }
    if (base.search && searchableProperties.length > 0) {
      return { ...base, search_properties: searchableProperties };
    }
    return base;
  }, [listState.filters, searchableProperties, builtInBaselineGroups]);

  // Track dynamically added columns (properties not in original config) and
  // hidden columns. Both declared here (before useCrmObjectListInfinite) so
  // requestedProperties can mirror the exact visible-column set the table
  // renders — added columns included, hidden columns excluded — keeping the
  // `/query` request as slim as the table actually needs.
  const [addedColumns, setAddedColumns] = useState<ColumnConfig[]>([]);
  const [hiddenColumnIds, setHiddenColumnIds] = useState<Set<string>>(
    new Set()
  );

  // Lookup columns whose target (association or target property) no longer
  // resolves. `isLookupColumnTargetResolved` is LENIENT while target schemas
  // are still loading, so a valid lookup is never dropped mid-load — it only
  // returns false once the association is gone or the loaded target schema
  // confirms the property doesn't exist. Degradation is render/projection-time
  // exclusion ONLY: the id stays in the saved columnOrder (see
  // svCaptureViewState) so a transiently-unresolvable schema never permanently
  // drops the column from the stored view.
  const degradedLookupIds = useMemo(() => {
    const s = new Set<string>();
    for (const col of [...config.columns, ...addedColumns]) {
      if (
        col.kind === 'lookup' &&
        !isLookupColumnTargetResolved(
          col,
          associationTypes,
          lookupTargetSchemasByType,
          objectTypesData?.allObjectTypes,
          objectTypesFetched,
          associationTypesFetched
        )
      ) {
        s.add(getColumnKey(col));
      }
    }
    return s;
  }, [
    config.columns,
    addedColumns,
    associationTypes,
    lookupTargetSchemasByType,
    objectTypesData?.allObjectTypes,
    objectTypesFetched,
    associationTypesFetched,
  ]);

  const availablePropertyNames = useMemo(
    () => new Set(Object.keys(propertySchemas)),
    [propertySchemas]
  );
  // Visible columns in config order — the display-ordered variant (used by the
  // table) is declared further down as `visibleColumns`; ordering is irrelevant
  // for deriving the request shape.
  const unorderedVisibleColumns = useMemo(
    () =>
      [...config.columns, ...addedColumns].filter(
        col =>
          !hiddenColumnIds.has(getColumnKey(col)) &&
          !degradedLookupIds.has(getColumnKey(col))
      ),
    [config.columns, addedColumns, hiddenColumnIds, degradedLookupIds]
  );
  const requestedProperties = useMemo(
    () =>
      deriveVisibleColumnProperties(
        unorderedVisibleColumns,
        availablePropertyNames
      ),
    [unorderedVisibleColumns, availablePropertyNames]
  );
  // Associations are opt-in on /query: name every association type a visible
  // column renders, or those cells silently show "—". MUST stay derivation-
  // identical to usePrefetchCRMList (same helper) or the prefetch warms a
  // different cache key than this page reads.
  const listOptions = useMemo(
    () => ({
      include_associations: deriveVisibleColumnAssociations(
        unorderedVisibleColumns
      ),
    }),
    [unorderedVisibleColumns]
  );

  // Kind-based lists (e.g. automations spanning multiple entity types) fetch
  // by `entity_kind` instead of a single `entityType`. Memoized so the query
  // key doesn't churn on a new object identity each render.
  const listRef = useMemo(
    () =>
      config.entityKind
        ? { entity_kind: config.entityKind }
        : config.entityType,
    [config.entityKind, config.entityType]
  );

  // Data fetching with infinite scroll
  const {
    data: response,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    totalCount,
    error: listError,
  } = useCrmObjectListInfinite(
    currentOrg?.id || 0,
    listRef,
    {
      ...filtersWithSearch,
      page_size: 50,
      // Request only the properties the visible columns render; identity/avatar
      // props are intersected with the type schema. Empty (schema not yet loaded)
      // omits `properties`, preserving the all-properties fallback.
      ...(requestedProperties.length > 0
        ? { properties: requestedProperties }
        : {}),
    },
    listOptions
  );

  const objects = useMemo(() => response?.results || [], [response?.results]);
  const total = totalCount;
  const associatedObjects = useMemo(
    () => response?.associated_objects || {},
    [response?.associated_objects]
  );

  const hasUnsortableSortError =
    isApiError(listError) &&
    listError.statusCode === 400 &&
    !!(listError.response as Record<string, unknown> | undefined)?.sort;

  // Attio-style infinite scroll: fetch when virtualizer renders unloaded rows
  const handleLoadRows = useCallback(
    (_startIndex: number, endIndex: number) => {
      // Fetch next page if we need data beyond what's loaded
      if (hasNextPage && !isFetchingNextPage && endIndex >= objects.length) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, objects.length, fetchNextPage]
  );

  // Flush store when entity type changes
  const prevEntityTypeRef = useRef(config.entityType);
  useEffect(() => {
    if (prevEntityTypeRef.current !== config.entityType) {
      clearCrmObjectStore();
      // The store is now empty, so any cached list entry (ids-only —
      // bodies live in the store) is a sentinel pointing at nothing, and
      // detail/slideout entries (staleTime: Infinity, never rewritten by
      // WS events — applyEvent.ts is store-only for these, see its module
      // doc comment) would otherwise survive as a status-only sentinel
      // whose store body is gone, for an object viewed under the previous
      // type — permanently stuck loading. Evict the unified list-key family
      // plus the detail/slideout families for the current org.
      const orgId = currentOrg?.id;
      queryClient.removeQueries({
        predicate: query => {
          const key = query.queryKey;
          if (isCrmListKey(key)) {
            return true;
          }
          if (
            !orgId ||
            !Array.isArray(key) ||
            key[0] !== 'crm' ||
            key[1] !== orgId
          ) {
            return false;
          }
          // Detail family: ['crm', orgId, 'objects', entityType, 'detail', ...]
          if (key[2] === 'objects' && key[4] === 'detail') {
            return true;
          }
          // Slideout family: ['crm', orgId, 'object', objectId]
          return key[2] === 'object';
        },
      });
      prevEntityTypeRef.current = config.entityType;
    }
  }, [config.entityType, currentOrg?.id, queryClient]);

  // Re-attach banner on page load if localStorage has a non-stale active job.
  useEffect(() => {
    if (!currentOrg || !user) {
      return;
    }
    const persisted = getActiveBulkActionJob(user.id, currentOrg.id);
    if (!persisted) {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const client = createCRMObjectClient(currentOrg.id, config.entityType);
        const job = await client.getBulkActionJob(persisted.jobId);
        if (cancelled) {
          return;
        }
        if (job.status === 'pending' || job.status === 'running') {
          // Prefer the job response's entity_type_name (authoritative) and
          // fall back to the persisted value for older entries written before
          // entityTypeName was added to localStorage.
          setActiveBulkActionJobState({
            jobId: persisted.jobId,
            totalCount: persisted.totalCount,
            entityTypeName:
              job.entity_type_name || persisted.entityTypeName || '',
          });
        } else {
          clearActiveBulkActionJob(user.id, currentOrg.id);
        }
      } catch (err) {
        const { statusCode } = err as { statusCode?: number };
        if (statusCode === 404) {
          clearActiveBulkActionJob(user.id, currentOrg.id);
        } else {
          logger.warn('Failed to revive bulk action job from localStorage', {
            component: 'crm-bulk-action',
            org_id: currentOrg.id,
            bulk_action_job_id: persisted.jobId,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentOrg, user, config.entityType]);

  // Real-time WebSocket updates flow through useCrmObjectListInfinite, which
  // already calls useStoreSelector(watchedIds) internally and produces a new
  // `objects` array reference whenever a watched store entry changes. That
  // invalidates the `tableRows` memo below, so a separate global store
  // subscription is unnecessary — and would defeat the per-entity subscription
  // by re-rendering on every store mutation in the org (e.g., during bulk
  // imports).

  // Reported by SchemaFilterBuilder — false while any condition's value is
  // malformed for its field type (e.g. non-email on an email "equals" filter).
  const [filtersAreValid, setFiltersAreValid] = useState(true);

  const filterFieldConfig = useMemo(
    () => ({
      filters: {
        validate: (val: unknown) => {
          const { conditions, valid } = val as {
            conditions: FilterCondition[];
            valid: boolean;
          };
          if (!valid) {
            return 'Some filter values are invalid';
          }
          if (hasIncompleteFilterConditions(conditions)) {
            return 'Some filter conditions are incomplete';
          }
          return undefined;
        },
      },
    }),
    []
  );

  const filterFormValues = useMemo(
    () => ({
      filters: {
        conditions: listState.filterConditions ?? [],
        valid: filtersAreValid,
      },
    }),
    [listState.filterConditions, filtersAreValid]
  );

  const filterGroups = listState.filterConditionGroups;

  const handleGroupsChange = useCallback(
    (next: FilterConditionGroup[]) => listState.setFilterConditionGroups(next),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- listState object recreates every render
    [listState.setFilterConditionGroups]
  );

  const [columnOrder, setColumnOrder] = useState<string[] | null>(null);
  // Only one column can be pinned at a time; default to the first column.
  // The pin toggle UI was removed along with the column-config slideout —
  // until a new affordance is wired up, pinning is fixed to the first
  // configured column.
  const pinnedColumnId = useMemo<string | null>(
    () => (config.columns[0] ? getColumnKey(config.columns[0]) : null),
    [config.columns]
  );
  const isDesktop = useBreakpoint('md');
  const effectivePinnedColumnIds = useMemo(
    () => (isDesktop && pinnedColumnId ? [pinnedColumnId] : []),
    [isDesktop, pinnedColumnId]
  );

  // ---------------------------------------------------------------------------
  // Add Column and Column Menu Actions
  // ---------------------------------------------------------------------------

  // Get the set of currently visible column keys (property names)
  const visibleColumnKeys = useMemo(() => {
    const keys = new Set<string>();
    // Include config columns that aren't hidden
    for (const col of config.columns) {
      if (!hiddenColumnIds.has(getColumnKey(col))) {
        keys.add(getColumnKey(col));
      }
    }
    // Include dynamically added columns that aren't hidden. Excluding hidden
    // added columns (mirroring the config-column treatment above) lets a hidden
    // dynamic lookup column drop out of `visibleColumnKeys`, so the add-column
    // picker re-offers it as restorable instead of leaving it permanently
    // unavailable for the current view.
    for (const col of addedColumns) {
      if (!hiddenColumnIds.has(getColumnKey(col))) {
        keys.add(getColumnKey(col));
      }
    }
    return keys;
  }, [config.columns, addedColumns, hiddenColumnIds]);

  // Available columns for the "+" add column dropdown.
  // Shows user-facing properties and associations from the schema, filtering
  // out ones already visible plus system/infrastructure noise (the name field
  // duplicate, internal-access fields, and form-managed or infra associations)
  // that has no business being a list column.
  const availableColumns = useMemo<AddColumnOption[]>(() => {
    const propertyOptions = Object.entries(propertySchemas)
      .filter(([propName, schema]) => {
        // Skip properties already visible
        if (visibleColumnKeys.has(propName)) {
          return false;
        }
        // Skip internal-only properties (not user visible)
        if (schema.value_read_access === 'internal') {
          return false;
        }
        // Skip what this list declares as never-a-column (engine-owned
        // JSON, runtime state, …) — see ListPageConfig.hiddenColumnOptions.
        if (config.hiddenColumnOptions?.includes(propName)) {
          return false;
        }
        // Skip the display-name field — it's already rendered as the
        // object_name column (whose key is "object_name", so the
        // visible-key check above misses it and it shows as a duplicate).
        // Only excludes ahoy_display_name; first/last/name remain selectable.
        if (propName === 'ahoy_display_name') {
          return false;
        }
        return true;
      })
      .map(([propName, schema]) => {
        const Icon = getPropertyTypeIconComponent(schema);
        return {
          id: propName,
          label: schema.label || propName,
          icon: Icon,
        };
      });

    // Only show associations where the source entity type matches current
    // entity type, excluding infra targets (import/file/team/pipeline/stage
    // timer/timeline) and form-managed system associations (creator, mentions)
    // that mirror the note link-picker filtering.
    const sourceAssociations = associationTypes.filter(
      at =>
        matchesSource(at, config.entityType) &&
        !config.hiddenColumnOptions?.includes(at.association_type_name) &&
        !EXCLUDED_LINK_TARGETS.has(at.target_entity_type_name) &&
        !at.target_entity_type_name.endsWith('_timeline') &&
        !SYSTEM_PICKER_ASSOCIATION_TYPES.has(at.association_type_name)
    );

    // Each association becomes a drill-in GROUP: selecting it opens a second
    // panel with the association column itself ("Add as column") plus one entry
    // per lookup target property — so the top level stays compact instead of
    // dumping every association's every property inline.
    const lookupsByAssociation = new Map<string, LookupColumnOption[]>();
    for (const opt of buildLookupColumnOptions({
      associations: sourceAssociations,
      objectTypes: objectTypesData?.allObjectTypes,
      targetSchemasByType: lookupTargetSchemasByType,
      visibleColumnKeys,
    })) {
      const parsed = parseLookupColumnName(opt.id);
      if (!parsed) {
        continue;
      }
      const existing = lookupsByAssociation.get(parsed.associationTypeName);
      if (existing) {
        existing.push(opt);
      } else {
        lookupsByAssociation.set(parsed.associationTypeName, [opt]);
      }
    }

    const associationGroups: AddColumnOption[] = [];
    for (const at of sourceAssociations) {
      const assocLabel = at.label || at.association_type_name;
      const children: AddColumnOption[] = [];
      // The association column itself (associated records as badges), unless it
      // is already visible.
      if (!visibleColumnKeys.has(at.association_type_name)) {
        children.push({
          id: at.association_type_name,
          label: 'Add as column',
          icon: Users01,
          // Read cleanly in flat search ("Employees"), not
          // "Employees → Add as column".
          searchLabel: assocLabel,
        });
      }
      // One entry per offerable lookup target property (buildLookupColumnOptions
      // already excludes internal-read, display-name, already-visible, and
      // unresolved-target properties).
      for (const opt of lookupsByAssociation.get(at.association_type_name) ??
        []) {
        children.push({
          id: opt.id,
          label:
            opt.propertySchema.label || opt.propertySchema.property_type_name,
          icon: getPropertyTypeIconComponent(opt.propertySchema),
        });
      }
      if (children.length > 0) {
        associationGroups.push({
          id: `group:${at.association_type_name}`,
          label: assocLabel,
          icon: Users01,
          children,
        });
      }
    }

    // Association groups first (A-Z), then regular properties (A-Z), instead of a
    // single interleaved A-Z sort — so drill-in association groups stay grouped
    // at the top of the add-column menu.
    associationGroups.sort((a, b) => a.label.localeCompare(b.label));
    propertyOptions.sort((a, b) => a.label.localeCompare(b.label));
    return [...associationGroups, ...propertyOptions];
  }, [
    propertySchemas,
    visibleColumnKeys,
    associationTypes,
    matchesSource,
    config.entityType,
    config.hiddenColumnOptions,
    lookupTargetSchemasByType,
    objectTypesData?.allObjectTypes,
  ]);

  // Add a column (either unhide existing or add new property/association column)
  const handleAddColumn = useCallback(
    (columnId: string) => {
      // Check if this column already exists — either as a saved config column
      // or as a previously-added dynamic column. A dynamic lookup column that
      // was added and then hidden is still present in `addedColumns`; the picker
      // now re-offers it (see `visibleColumnKeys`), so re-adding it must UNHIDE
      // the existing column rather than append a duplicate to `addedColumns`.
      const existsAlready =
        config.columns.some(col => getColumnKey(col) === columnId) ||
        addedColumns.some(col => getColumnKey(col) === columnId);

      if (existsAlready) {
        // Unhide existing column
        setHiddenColumnIds(prev => {
          const next = new Set(prev);
          next.delete(columnId);
          return next;
        });
      } else {
        // Lookup compound id "<assoc>.<targetProp>" where <assoc> is a known association.
        const lookupCol = reconstructLookupColumnFromSavedId(
          columnId,
          associationTypes
        );
        if (lookupCol) {
          setAddedColumns(prev => [
            ...prev,
            {
              ...lookupCol,
              sortable: isLookupColumnSortable(
                lookupCol,
                associationTypes,
                config.entityType,
                matchesSource,
                lookupTargetSchemasByType,
                objectTypesData?.allObjectTypes,
                isFieldTypeSortable
              ),
            },
          ]);
        } else {
          // Determine the column kind: association or property
          const isAssociation = associationTypes.some(
            at => at.association_type_name === columnId
          );
          setAddedColumns(prev => [
            ...prev,
            {
              kind: isAssociation ? 'association' : 'property',
              name: columnId,
            } as ColumnConfig,
          ]);
        }

        // Persist the newly added column into an already-initialized explicit
        // column order. While `columnOrder` is null the `fullColumnOrder`
        // fallback (config + added) already covers added columns, so leave it
        // null. But once a saved view is applied or columns are reordered,
        // `columnOrder` becomes an explicit list, and a key missing from it is
        // sorted to the end this session AND dropped on save/reload (see
        // svCaptureViewState). Append the id so the column survives persistence.
        setColumnOrder(prev =>
          prev && !prev.includes(columnId) ? [...prev, columnId] : prev
        );
      }
    },
    [
      config.columns,
      config.entityType,
      addedColumns,
      associationTypes,
      matchesSource,
      lookupTargetSchemasByType,
      objectTypesData?.allObjectTypes,
      isFieldTypeSortable,
    ]
  );

  // Navigate to CRM settings to create a new attribute
  const handleCreateAttribute = useCallback(() => {
    if (currentOrg) {
      navigate(
        `/${currentOrg.id}/settings/objects-fields?tab=${config.entityType}`
      );
    }
  }, [config.entityType, currentOrg, navigate]);

  // Hide a column
  const handleColumnHide = useCallback((columnId: string) => {
    setHiddenColumnIds(prev => new Set([...prev, columnId]));
  }, []);

  // ---------------------------------------------------------------------------
  // Slideout
  // ---------------------------------------------------------------------------
  // App-level surface: opening a record from the list REPLACES the open
  // slideouts (openSlideout) — only clicks from inside a slideout stack.
  const { stack, topId, openSlideout } = useSlideoutStack();
  // Any slideout (entity, proposal, email-thread) — used to auto-close the
  // filter panel so it doesn't sit open behind a slideout regardless of kind.
  const hasAnySlideoutOpen = stack.length > 0;
  // Entity-only — used to gate keyboard-nav (↑/↓ to step through records).
  // Other kinds (e.g. proposals) shouldn't hijack arrow keys for record
  // navigation.
  const hasEntitySlideoutOpen = topId !== undefined;

  // Close filters when ANY slideout opens
  useEffect(() => {
    if (hasAnySlideoutOpen && listState.showAdvancedFilters) {
      listState.setShowAdvancedFilters(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- listState object recreates every render
  }, [hasAnySlideoutOpen, listState.showAdvancedFilters]);

  const handleViewObject = useCallback(
    (object: CrmObject) => {
      if (onViewObject) {
        onViewObject(object);
      } else {
        openSlideout(object.entity_id);
      }
    },
    [onViewObject, openSlideout]
  );

  // ---------------------------------------------------------------------------
  // Delete confirmation modal state
  // ---------------------------------------------------------------------------
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    count: number;
    objectName: string;
    title?: string;
    description?: string;
    onConfirm: () => Promise<void>;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState(false);
  const [bulkUpdateSelectedObjects, setBulkUpdateSelectedObjects] = useState<
    CrmObject[]
  >([]);
  const [activeBulkActionJob, setActiveBulkActionJobState] = useState<{
    jobId: number;
    totalCount: number;
    entityTypeName: string;
  } | null>(null);

  // Merge wizard state
  const [mergeWizardOpen, setMergeWizardOpen] = useState(false);
  const [mergeEntityIds, setMergeEntityIds] = useState<[string, string] | null>(
    null
  );

  // ---------------------------------------------------------------------------
  // Unsaved changes guard (for inline editing in progress)
  // ---------------------------------------------------------------------------
  const [isEditing, setIsEditing] = useState(false);
  const [editingObjectId, setEditingObjectId] = useState<string | null>(null);

  const handleEndEdit = useCallback(() => {
    setIsEditing(false);
    setEditingObjectId(null);
  }, []);

  const { guard: unsavedGuard, modalProps: unsavedModalProps } =
    useUnsavedChangesGuard({
      hasUnsavedChanges: isEditing,
      onDiscard: handleEndEdit,
      // Inline edits are committed on blur/Enter — no async save path needed here
      blockRouterNavigation: false,
      blockBrowserClose: false,
    });

  // Guard-wrapped row click: shows dialog if a different row is being edited
  const handleRowClick = useCallback(
    (object: CrmObject) => {
      if (isEditing && editingObjectId !== object.entity_id) {
        unsavedGuard(() => handleViewObject(object));
      } else {
        handleViewObject(object);
      }
    },
    [isEditing, editingObjectId, unsavedGuard, handleViewObject]
  );

  // ---------------------------------------------------------------------------
  // Keyboard navigation for slideout (↑ / ↓ to go to prev/next record)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!hasEntitySlideoutOpen) {
      return;
    }

    const currentIndex = objects.findIndex(o => o.entity_id === topId);

    const handler = (e: KeyboardEvent) => {
      // Skip when user is typing in an input
      const active = document.activeElement;
      const isInputFocused =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active instanceof HTMLSelectElement ||
        (active instanceof HTMLElement && active.isContentEditable);

      if (isInputFocused) {
        return;
      }

      if (e.key === 'ArrowDown') {
        if (currentIndex >= 0 && currentIndex < objects.length - 1) {
          e.preventDefault();
          const next = objects[currentIndex + 1];
          const navigate = () => {
            openSlideout(next.entity_id);
          };
          if (unsavedGuard(navigate)) {
            navigate();
          }
        }
      } else if (e.key === 'ArrowUp') {
        if (currentIndex > 0) {
          e.preventDefault();
          const prev = objects[currentIndex - 1];
          const navigate = () => {
            openSlideout(prev.entity_id);
          };
          if (unsavedGuard(navigate)) {
            navigate();
          }
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [hasEntitySlideoutOpen, topId, objects, openSlideout, unsavedGuard]);

  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  const visibleColumns = useMemo(() => {
    // Combine config columns with dynamically added columns
    const allColumns = [...config.columns, ...addedColumns];
    const filtered = allColumns.filter(
      col =>
        !hiddenColumnIds.has(getColumnKey(col)) &&
        !degradedLookupIds.has(getColumnKey(col))
    );
    return sortColumnsByOrder(filtered, columnOrder, getColumnKey);
  }, [
    config.columns,
    addedColumns,
    hiddenColumnIds,
    degradedLookupIds,
    columnOrder,
  ]);

  // Currently visible columns, in display order, mapped to the export
  // `columns` payload contract — keeps the exported CSV in sync with the table.
  const exportColumns = useMemo(
    () => buildExportColumns(visibleColumns),
    [visibleColumns]
  );

  // Full known column set (config + dynamically added), UNFILTERED by
  // hiddenColumnIds. Used as the columnOrder fallback whenever columnOrder is
  // null — falling back to visibleColumns (which is hidden-filtered) would
  // silently drop hidden columns' remembered positions from the order.
  const fullColumnOrder = useMemo(
    () => [...config.columns, ...addedColumns].map(getColumnKey),
    [config.columns, addedColumns]
  );

  // Drag-reorder or keyboard/menu reorder from the DataTable
  const handleColumnReorder = useCallback(
    (rightPaneOrder: string[]) => {
      setColumnOrder(prev =>
        reconcileColumnOrder(prev ?? fullColumnOrder, rightPaneOrder)
      );
    },
    [fullColumnOrder]
  );

  // ---------------------------------------------------------------------------
  // DataTable column and row data
  // ---------------------------------------------------------------------------

  const isColumnSortable = useCallback(
    (column: ColumnConfig): boolean => {
      // Lookup columns are sortable only when single-target (many_to_one/one_to_one),
      // non-transitive, and their source matches the entity type being viewed; the
      // backend sorts by the associated object's property via the compound sort key
      // (= the column's name). Many-valued lookups are not offered. The add-column
      // and saved-view-restore paths also precompute a `sortable` value on lookup
      // columns, but it's inert here — this branch is checked before the
      // explicit-override branch below, so it's this call that's authoritative.
      if (column.kind === 'lookup') {
        return isLookupColumnSortable(
          column,
          associationTypes,
          config.entityType,
          matchesSource,
          lookupTargetSchemasByType,
          objectTypesData?.allObjectTypes,
          isFieldTypeSortable
        );
      }

      // If sortable is explicitly set, respect it
      if (column.sortable !== undefined) {
        return column.sortable;
      }

      // Handle property columns based on field type sortability
      if (column.kind === 'property' && column.name) {
        if (!fieldTypes || !propertySchemas) {
          return false;
        }
        const propertyType = propertySchemas[column.name];
        if (!propertyType) {
          return false;
        }
        return isFieldTypeSortable(propertyType.field_type_name);
      }

      // Association columns: sortability is declared by the backend via the
      // `sortable` flag on the association-types schema (computed by the same
      // predicate the server validates + executes with). Read it directly so the
      // UI can never offer a sort the backend rejects. Missing/undefined (stale
      // cached schema) → not sortable (fail closed). The explicit column-config
      // `sortable` override is handled at the top of this function and still wins.
      if (column.kind === 'association') {
        const assoc = associationTypes.find(
          at => at.association_type_name === column.name
        );
        if (!assoc) {
          return false;
        }
        return assoc.sortable === true;
      }

      return false;
    },
    [
      fieldTypes,
      propertySchemas,
      associationTypes,
      config.entityType,
      matchesSource,
      lookupTargetSchemasByType,
      objectTypesData?.allObjectTypes,
      isFieldTypeSortable,
    ]
  );

  const tableColumns = useMemo<DataTableColumnConfig[]>(
    () =>
      visibleColumns.map(col => ({
        ...col,
        name: getColumnKey(col),
        label: getColumnLabel(
          col,
          config.title,
          propertySchemas,
          associationTypes,
          lookupTargetSchemasByType,
          objectTypesData?.allObjectTypes
        ),
        sortable: isColumnSortable(col),
      })),
    [
      visibleColumns,
      config.title,
      propertySchemas,
      associationTypes,
      lookupTargetSchemasByType,
      objectTypesData?.allObjectTypes,
      isColumnSortable,
    ]
  );

  const tableRows = useMemo<DataTableRowData[]>(
    () =>
      objects.map(obj => {
        // `obj` is already enriched with store data by useCrmObjectListInfinite
        // (via useStoreSelector). The getStoreObject fallback covers the edge
        // case where a WS-only entry exists in the store but the hook's
        // enrichment guards rejected it (e.g., fetchedAt === 0). Without the
        // global store subscription that previously triggered this memo, this
        // path is now best-effort: it shows whatever was in the store at the
        // time `objects` was last produced. That is correct for live updates
        // because any change to a watched store entry produces a new `objects`
        // reference, which re-runs this memo.
        const liveObj =
          (getStoreObject(obj.entity_id) as CrmObject | undefined) ?? obj;
        return {
          id: liveObj.entity_id,
          rowId: liveObj.entity_id,
          cells: Object.fromEntries(
            visibleColumns.map(col => {
              const colKey = getColumnKey(col);
              return [
                colKey,
                renderColumnCell(
                  col,
                  liveObj,
                  propertySchemas,
                  associatedObjects,
                  false, // No bold styling - object_name kind handles primary display
                  {
                    orgId: currentOrg?.id,
                    lookupTargetSchemasByType,
                    entityTypeLabels,
                  }
                ),
              ];
            })
          ),
        };
      }),
    [
      objects,
      visibleColumns,
      propertySchemas,
      associatedObjects,
      currentOrg?.id,
      lookupTargetSchemasByType,
      entityTypeLabels,
    ]
  );

  // Centralised single-property save: optimistic store + detail-cache writes,
  // seq-guarded rollback, and 409 conflict retry all live in the hook. The
  // object is passed per mutate call, so a save committed via delayed blur
  // always targets the row it was edited on.
  const { mutateAsync: updateCrmPropertyAsync } = useUpdateCrmProperty(
    currentOrg?.id,
    user?.id
  );
  // Association counterpart: replaces one association type's full target set
  // (diffed into add/remove events inside the hook).
  const { mutateAsync: updateCrmAssociationAsync } = useUpdateCrmAssociation(
    currentOrg?.id,
    user?.id
  );

  const handleInlineAssociationSave = useCallback(
    async (
      entityId: string,
      associationTypeName: string,
      value: unknown
    ): Promise<void> => {
      const object = objects.find(o => o.entity_id === entityId);
      if (!object) {
        throw new Error('Object not found');
      }
      const targetIds = Array.isArray(value) ? (value as string[]) : [];
      try {
        await updateCrmAssociationAsync({
          object,
          associationTypeName,
          targetIds,
        });
      } catch (err) {
        logger.warn('Inline association save failed', {
          entityId,
          associationTypeName,
          entityType: config.entityType,
          error: err,
        });
        // Rethrow so EditableCell keeps the cell open and shows the message.
        throw err;
      }
    },
    [objects, updateCrmAssociationAsync, config.entityType]
  );

  const handleInlineSave = useCallback(
    async (
      entityId: string,
      propertyName: string,
      value: unknown
    ): Promise<void> => {
      const object = objects.find(o => o.entity_id === entityId);
      if (!object) {
        throw new Error('Object not found');
      }
      try {
        await updateCrmPropertyAsync({ object, propertyName, value });
      } catch (err) {
        logger.warn('Inline cell save failed', {
          entityId,
          propertyName,
          entityType: config.entityType,
          error: err,
        });
        // Rethrow so EditableCell keeps the cell open and shows the message.
        throw err;
      }
    },
    [objects, updateCrmPropertyAsync, config.entityType]
  );

  // Inline cell editing (Attio-style): single click selects, double click /
  // Enter / F2 edits. Returning null keeps a cell read-only — clicks on it
  // bubble to the row and open the record as before.
  const getCellEditConfig = useCallback(
    (entityId: string, colId: string) => {
      // Entity-level gate: registry read-only types and users without any
      // update permission get no edit affordance at all. Property and
      // association columns each re-check their own permission below.
      if (!canUpdateProperties && !canUpdateAssociations) {
        return null;
      }
      const col = visibleColumns.find(c => getColumnKey(c) === colId);
      if (!col) {
        return null;
      }
      // Association columns (e.g. Works At): edit the target set inline with
      // the canonical association picker. The hook diffs the picker's value
      // against the object's current edges into add/remove events.
      if (col.kind === 'association') {
        if (!canUpdateAssociations || !currentOrg?.id) {
          return null;
        }
        const associationType = associationTypes.find(
          at => at.association_type_name === col.name
        );
        if (!associationType) {
          return null;
        }
        // Schema-level gate: internal association types (email threads,
        // meetings, transitive chains…) are system-managed — the backend
        // rejects user-sourced events on them, so offer no editor. Mirrors
        // isPropertyEditable for the property branch below.
        if (associationType.value_write_access !== 'all') {
          return null;
        }
        const object = objects.find(o => o.entity_id === entityId);
        if (!object) {
          return null;
        }
        const targetIds = (object.associations ?? [])
          .filter(a => a.association_type_name === col.name)
          .map(a => a.target_entity_id);
        // Write-once associations freeze once set (see associationEditability):
        // the backend rejects any change, so offer no inline editor. Unlike the
        // gates above this is per-ROW, not per-column — a record that has no
        // target yet stays editable, because the first add is permitted.
        if (isAssociationValueFrozen(associationType, targetIds.length > 0)) {
          return null;
        }
        // Stage associations (pipeline / lifecycle stage) are required — an
        // entity always sits on some stage, so removal is disallowed (the
        // picker hides the "No …" clear row and the ✕). Detected by the
        // target being a stage-kind entity type.
        const isStageAssociation = isStageTargetKind(
          associationType.target_entity_type_name,
          entityKindsMap
        );
        return {
          association: {
            associationType,
            orgId: currentOrg.id,
            allowClear: !isStageAssociation,
          },
          value: targetIds,
          columnLabel: getColumnLabel(
            col,
            config.title,
            propertySchemas,
            associationTypes
          ),
          onSave: (value: unknown) =>
            handleInlineAssociationSave(entityId, col.name ?? '', value),
        };
      }
      if (col.kind !== 'property' || !canUpdateProperties) {
        return null;
      }
      // Display-name properties are the record's identity and its primary
      // click-to-open affordance (custom-entity lists render
      // ahoy_display_name as the name column). Keep them read-only inline —
      // rename via the record view. Mirrors useUpdatableFields' exclusion.
      if (DISPLAY_NAME_FIELDS.has(col.name ?? '')) {
        return null;
      }
      const schema = propertySchemas[col.name ?? ''];
      if (!schema) {
        return null;
      }
      // Schema-level gate: system-managed and immutable-after-create
      // properties (e.g. ahoy_updated_at) render read-only — same rule as
      // the record overview's edit mode.
      if (!isPropertyEditable(schema)) {
        return null;
      }
      const object = objects.find(o => o.entity_id === entityId);
      if (!object) {
        return null;
      }
      const label = getColumnLabel(
        col,
        config.title,
        propertySchemas,
        associationTypes
      );
      return {
        propertyType: schema,
        // getPropertyValue, not raw `.value`: when the list request carries an
        // include_associations projection, the backend strips the {value: x}
        // wrapper and properties arrive as raw values — a bare `.value` read
        // yields undefined and blanks the cell.
        value: getPropertyValue(object.properties?.[col.name ?? '']) ?? null,
        columnLabel: label,
        onSave: (value: unknown) =>
          handleInlineSave(entityId, col.name ?? '', value),
      };
    },
    [
      canUpdateProperties,
      canUpdateAssociations,
      currentOrg?.id,
      visibleColumns,
      propertySchemas,
      objects,
      config.title,
      associationTypes,
      entityKindsMap,
      handleInlineSave,
      handleInlineAssociationSave,
    ]
  );

  // ---------------------------------------------------------------------------
  // Bulk actions
  // ---------------------------------------------------------------------------

  const { selectedKeys, setSelectedKeys, setExcludedIds, excludedIds } =
    listState;

  // Handle selection changes - deselecting from "all" keeps 'all' + tracks exclusions
  const handleSelectionChange = useCallback(
    (newSelection: 'all' | Set<string | number>) => {
      if (newSelection === 'all') {
        // Selecting all - clear any exclusions
        setExcludedIds(new Set());
        setSelectedKeys('all');
      } else if (selectedKeys === 'all') {
        // Was "all" mode - check if user is deselecting ALL (empty set = header checkbox click)
        if (newSelection.size === 0) {
          // Header checkbox clicked to deselect all - exit 'all' mode completely
          setExcludedIds(new Set());
          setSelectedKeys(new Set());
          return;
        }
        // User deselected or re-selected individual item(s) — stay in 'all' mode
        // and track exclusions so "all except these" semantics apply across full dataset
        const currentPageIds = objects.map(o => o.entity_id);
        const deselectedIds = currentPageIds.filter(
          id => !newSelection.has(id) && !excludedIds.has(id)
        );
        const reselectedSet = new Set<string | number>(
          currentPageIds.filter(
            id => newSelection.has(id) && excludedIds.has(id)
          )
        );
        const updatedExclusions = new Set(
          [...excludedIds, ...deselectedIds].filter(
            id => !reselectedSet.has(id)
          )
        );
        setSelectedKeys('all');
        setExcludedIds(updatedExclusions);
      } else {
        // Normal selection (not "all" mode)
        setExcludedIds(new Set());
        setSelectedKeys(newSelection);
      }
    },
    [selectedKeys, setSelectedKeys, setExcludedIds, excludedIds, objects]
  );

  // Whether we're in "select all" mode
  const isSelectAllMode = listState.selectedKeys === 'all';

  // Effective count for bulk action bar
  const effectiveSelectedCount =
    listState.selectedKeys === 'all'
      ? (total ?? 0) - excludedIds.size
      : listState.selectedKeys.size;

  const selectedIdsArray = useMemo(() => {
    if (listState.selectedKeys === 'all') {
      // Filter out excluded IDs to maintain "all except these" semantics
      return objects.map(o => o.entity_id).filter(id => !excludedIds.has(id));
    }
    return Array.from(listState.selectedKeys).map(String);
  }, [listState.selectedKeys, objects, excludedIds]);

  // Export — declared requirement: the crm_export feature.
  const canExport = hasFeature('crm_export');
  const {
    exportObjects,
    isExporting,
    error: exportError,
  } = useCrmExport(currentOrg?.id ?? 0);

  useEffect(() => {
    if (exportError) {
      toast.error(exportError);
    }
  }, [exportError]);

  const handleExport = useCallback(async () => {
    // Distinguish "no representable visible columns" from "no column selection".
    // buildExportColumns drops non-representable kinds (object_name, association
    // name-chips), so a non-empty visible set can still yield zero export
    // columns when the user has hidden every property/lookup column. Passing
    // `columns: undefined` in that case makes the server fall back to the
    // default full-property export — a download that does NOT match the
    // narrowed table. object_name can't be mapped to a representable property
    // (the exportable types' ahoy_display_name is internal-read-gated), so
    // prevent the mismatched full dump and tell the user instead.
    if (visibleColumns.length > 0 && exportColumns.length === 0) {
      toast.error(
        'Add at least one property or lookup column to export this view.'
      );
      return;
    }
    try {
      // Determine which selection mode we're in and pass appropriate IDs
      if (isSelectAllMode) {
        // "All except these" mode - pass exclusions if any
        const excludedEntityIds =
          excludedIds.size > 0
            ? Array.from(excludedIds).map(String)
            : undefined;
        await exportObjects({
          entity_type_name: config.entityType,
          filter_groups: filtersWithSearch.filter_groups,
          search: filtersWithSearch.search,
          search_properties: filtersWithSearch.search_properties,
          excluded_entity_ids: excludedEntityIds,
          columns: exportColumns.length > 0 ? exportColumns : undefined,
        });
      } else if (
        listState.selectedKeys !== 'all' &&
        listState.selectedKeys.size > 0
      ) {
        // Individual selection mode - pass specific entity IDs
        await exportObjects({
          entity_type_name: config.entityType,
          filter_groups: filtersWithSearch.filter_groups,
          search: filtersWithSearch.search,
          search_properties: filtersWithSearch.search_properties,
          entity_ids: Array.from(listState.selectedKeys).map(String),
          columns: exportColumns.length > 0 ? exportColumns : undefined,
        });
      } else {
        // No selection - export all (with filters applied)
        await exportObjects({
          entity_type_name: config.entityType,
          filter_groups: filtersWithSearch.filter_groups,
          search: filtersWithSearch.search,
          search_properties: filtersWithSearch.search_properties,
          columns: exportColumns.length > 0 ? exportColumns : undefined,
        });
      }
    } catch {
      // Error feedback already handled by useEffect on exportError
    }
  }, [
    exportObjects,
    config.entityType,
    filtersWithSearch,
    isSelectAllMode,
    excludedIds,
    listState.selectedKeys,
    exportColumns,
    visibleColumns,
  ]);

  // Actual bulk delete logic (called after confirmation)
  const performBulkDelete = useCallback(async () => {
    if (!currentOrg || !user) {
      return;
    }
    const client = createCRMObjectClient(currentOrg.id, config.entityType);

    // Every selection — explicit rows or select-all — goes through the ONE
    // bulk endpoint. Never fan out a request per row: the events endpoint is
    // throttled per org (100/min), so a per-row fan-out trips the throttle
    // partway through a large selection and the rejected rows surface to the
    // user as "couldn't delete some records".
    const excludedEntityIds =
      excludedIds.size > 0 ? Array.from(excludedIds).map(String) : undefined;

    let selection: BulkActionSelection;
    if (!isSelectAllMode) {
      if (selectedIdsArray.length === 0) {
        return;
      }
      selection = { kind: 'entity_ids', entityIds: selectedIdsArray };
    } else {
      const hasFilters =
        !!filtersWithSearch.filter_groups &&
        filtersWithSearch.filter_groups.length > 0;
      const hasSearch =
        filtersWithSearch.search != null && filtersWithSearch.search !== '';
      selection =
        hasFilters || hasSearch
          ? {
              kind: 'filters',
              filterGroups: filtersWithSearch.filter_groups,
              excludedEntityIds,
              search: filtersWithSearch.search,
              searchProperties: filtersWithSearch.search_properties,
            }
          : // Master checkbox with no filter or search — explicit opt-in to
            // "every record of this type".
            { kind: 'all', excludedEntityIds };
    }

    try {
      const totalSelected = effectiveSelectedCount;
      let deletedSoFar = 0;
      const MAX_ITERATIONS = 20; // cap protects against a runaway loop

      try {
        // First call distinguishes sync vs async. An explicit entity_ids
        // selection is resolved server-side in one request and always comes
        // back has_more=false, so the loop below runs exactly once for it.
        const firstResponse = await client.bulkDelete(
          config.entityType,
          selection
        );

        if (firstResponse.mode === 'async') {
          // Backend dispatched a Celery job; hand off to the banner.
          setActiveBulkActionJobState({
            jobId: firstResponse.job_id,
            totalCount: firstResponse.total_count,
            entityTypeName: config.entityType,
          });
          setActiveBulkActionJob(
            user.id,
            currentOrg.id,
            firstResponse.job_id,
            firstResponse.total_count,
            config.entityType
          );
          setSelectedKeys(new Set());
          setExcludedIds(new Set());
          return;
        }

        // Sync path — continue the chunked loop, seeded with the first response.
        deletedSoFar = firstResponse.deleted_count;
        let hasMore = firstResponse.has_more;
        let iterations = 1;

        while (hasMore) {
          iterations += 1;
          if (iterations > MAX_ITERATIONS) {
            // Don't fall through to success: matching records likely remain.
            // The catch block reports partial progress and prompts a retry.
            throw new Error('bulk_delete_iteration_limit_reached');
          }
          const response = await client.bulkDelete(
            config.entityType,
            selection
          );
          if (response.mode !== 'sync') {
            // Defensive: backend should not switch modes mid-loop. Bail out.
            break;
          }
          deletedSoFar += response.deleted_count;
          hasMore = response.has_more;
        }
      } catch (err) {
        // Mid-loop failure. Extract error_type if present on the thrown api error.
        const apiError = err as { response?: { error_type?: string } };
        const errorType = apiError?.response?.error_type;
        if (deletedSoFar > 0) {
          toast.error(
            `Deleted ${deletedSoFar} of ${totalSelected} ${(config.pluralTitle || `${config.title}s`).toLowerCase()}. Click Delete again to finish.`
          );
        } else {
          toast.error(translateBulkDeleteError(errorType));
        }
        setSelectedKeys(new Set());
        setExcludedIds(new Set());
        await refetch();
        return;
      }

      // All chunks processed successfully.
      if (deletedSoFar === 1) {
        toast.success(`${config.title} deleted`);
      } else {
        toast.success(
          `${deletedSoFar} ${(config.pluralTitle || `${config.title}s`).toLowerCase()} deleted`
        );
      }

      setSelectedKeys(new Set());
      setExcludedIds(new Set());
      await refetch();
    } catch {
      // Backstop for anything outside the delete loop (which reports its own
      // errors above) — e.g. a failed refetch after a successful delete.
      toast.error("We couldn't delete these. Try again.");
    }
  }, [
    currentOrg,
    config.entityType,
    config.title,
    config.pluralTitle,
    effectiveSelectedCount,
    excludedIds,
    filtersWithSearch,
    isSelectAllMode,
    setSelectedKeys,
    setExcludedIds,
    refetch,
    selectedIdsArray,
    setActiveBulkActionJobState,
    user,
  ]);

  // Show confirmation modal before bulk delete
  const handleBulkDelete = useCallback(() => {
    const count = effectiveSelectedCount;
    if (count === 0) {
      return;
    }

    const entityLabel = config.title.toLowerCase();
    const pluralLabel = (
      config.pluralTitle || `${config.title}s`
    ).toLowerCase();
    const formattedCount = count.toLocaleString();
    const objectName =
      count === 1 ? `this ${entityLabel}` : `${formattedCount} ${pluralLabel}`;

    // For bulk operations (count > 1), surface the count and plural type in
    // the title so users see exactly what they're about to delete. Single-row
    // deletes use the default `Delete ${entityLabel}` title.
    const title =
      count > 1 ? `Delete ${formattedCount} ${pluralLabel}?` : undefined;

    const description =
      count > BULK_DELETE_ASYNC_THRESHOLD
        ? 'This runs in the background and may take a few minutes.'
        : undefined;

    setDeleteConfirmation({
      isOpen: true,
      count,
      objectName,
      title,
      description,
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          await performBulkDelete();
        } finally {
          setIsDeleting(false);
          setDeleteConfirmation(null);
        }
      },
    });
  }, [
    effectiveSelectedCount,
    config.title,
    config.pluralTitle,
    performBulkDelete,
  ]);

  const handleDeleteModalOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setDeleteConfirmation(null);
      }
    },
    [setDeleteConfirmation]
  );

  const handleBannerDismiss = useCallback(() => {
    setActiveBulkActionJobState(null);
    if (user && currentOrg) {
      clearActiveBulkActionJob(user.id, currentOrg.id);
    }
    void refetch();
  }, [user, currentOrg, refetch]);

  // ---------------------------------------------------------------------------
  // Row context actions (three-dot menu per row)
  // ---------------------------------------------------------------------------
  const getRowContextActions = useCallback(
    (rowId: string) => {
      const obj = objects.find(o => o.entity_id === rowId);

      const onEdit = obj
        ? () => {
            handleViewObject(obj);
          }
        : undefined;

      const onDelete =
        enableDelete && canDeleteObject && obj && currentOrg && user
          ? () => {
              // Get display name from object properties. getPropertyValue
              // handles both wrapped {value: x} and stripped raw shapes (the
              // latter when the list request carries include_associations).
              const displayName =
                getPropertyValue(obj.properties?.ahoy_display_name) ||
                getPropertyValue(obj.properties?.name) ||
                getPropertyValue(obj.properties?.first_name) ||
                'this item';

              setDeleteConfirmation({
                isOpen: true,
                count: 1,
                objectName: String(displayName),
                onConfirm: async () => {
                  setIsDeleting(true);
                  // Capture the org/session generation at dispatch so a late 202
                  // that resolves after an org switch/logout is rejected.
                  const dispatchGeneration =
                    currentSingleObjectDeleteJobGeneration();
                  try {
                    const client = createCRMObjectClient(
                      currentOrg.id,
                      config.entityType
                    );
                    const freshObject = await client.get(obj.entity_id);
                    const res = await submitEventsOptimistic({
                      orgId: currentOrg.id,
                      userId: user.id,
                      entityTypeName: freshObject.entity_type_name,
                      primaryEntityId: freshObject.entity_id,
                      fallbackSeq: freshObject.last_event_seq,
                      errorToast: false,
                      build: b =>
                        b.objectDeleted(
                          freshObject.entity_id,
                          freshObject.entity_type_name
                        ),
                    });
                    if (isAsyncSingleObjectDeleteResponse(res)) {
                      // 202: optimistic removal stays applied; the app-level
                      // host shows progress and handles success/rollback. Do
                      // NOT refetch (that would re-show the row mid-job).
                      startSingleObjectDeleteJob(
                        {
                          orgId: currentOrg.id,
                          jobId: res.job_id,
                          totalCount: res.total_count,
                          entityTypeName: freshObject.entity_type_name,
                          entityId: freshObject.entity_id,
                          recordName: String(displayName),
                        },
                        dispatchGeneration
                      );
                    } else {
                      await refetch();
                    }
                  } catch {
                    toast.error("We couldn't delete this item. Try again.");
                  } finally {
                    setIsDeleting(false);
                    setDeleteConfirmation(null);
                  }
                },
              });
            }
          : undefined;

      // Same gate as the bulk bar: each row action offers itself only when its
      // declared `requires` (or the fail-closed entity-write default) is met,
      // so a read-only seat never sees Create deal / Enrich in the row menu.
      const configRowActions = (config.rowActions ?? []).filter(action =>
        isBulkActionRequirementMet(
          action.requires ?? {
            permission: [config.entityType, 'ahoy_property_updated'],
          },
          { hasFeature, hasPermission }
        )
      );

      const extraActions: RowContextMenuAction[] =
        obj && configRowActions.length > 0
          ? configRowActions.map(action => ({
              key: action.id,
              label: action.label,
              icon: action.icon as React.FC<{ className?: string }> | undefined,
              isDestructive: action.variant === 'danger',
              isDisabled: action.isDisabled?.(obj) ?? false,
              onAction: () => action.onClick(obj),
            }))
          : [];

      // Custom rowActions are appended to built-ins. Built-in actions are only
      // suppressed when a custom action has the SAME id (e.g., 'copy-link' or
      // 'delete') — that lets a config override a specific built-in without
      // forfeiting the others. Without this, simply adding an enrichment row
      // action to contacts would silently strip the Delete option.
      const customActionIds = new Set(configRowActions.map(a => a.id));
      const overridesCopyLink = customActionIds.has('copy-link');
      const overridesDelete = customActionIds.has('delete');

      const onCopyLink = overridesCopyLink
        ? undefined
        : () => {
            const url = `${window.location.origin}${window.location.pathname}?slideoutEntityId=${rowId}`;
            void navigator.clipboard.writeText(url);
          };

      // "Open record" arrow → full record page. Only for the core CRM objects
      // (contact/company/deal) that have a full-page route today.
      const fullPageSlug = FULL_PAGE_ROUTE_SLUGS[config.entityType];
      const onOpen =
        fullPageSlug && currentOrg
          ? () => navigate(`/${currentOrg.id}/crm/${fullPageSlug}/${rowId}`)
          : undefined;

      return {
        onEdit,
        onDelete: overridesDelete ? undefined : onDelete,
        onCopyLink,
        onOpen,
        extraActions,
      };
    },
    [
      objects,
      enableDelete,
      canDeleteObject,
      currentOrg,
      user,
      config.entityType,
      config.rowActions,
      hasFeature,
      hasPermission,
      refetch,
      handleViewObject,
      navigate,
    ]
  );

  // ---------------------------------------------------------------------------
  // Create slideout
  // ---------------------------------------------------------------------------
  const handleCreateObject = useCallback(
    async (data: Record<string, unknown>) => {
      if (!currentOrg || !onCreateObject) {
        return;
      }
      await onCreateObject(currentOrg.id, data);
      void refetch();
    },
    [currentOrg, onCreateObject, refetch]
  );

  const handleOpenCreateModal = useCallback(() => {
    if (!currentOrg || !onCreateObject) {
      return;
    }
    const createTitle = `Create ${config.title.toLowerCase()}`;
    openCreateSlideout(
      buildGenericCreateConfig({
        orgId: currentOrg.id,
        entityType: config.entityType,
        title: createTitle,
        createProperties: config.createProperties || [],
        createAssociations: config.createAssociations,
        onSubmit: handleCreateObject,
        submitLabel: createTitle,
        onPropertyChange: onCreatePropertyChange,
      })
    );
  }, [
    currentOrg,
    config,
    handleCreateObject,
    openCreateSlideout,
    onCreateObject,
    onCreatePropertyChange,
  ]);

  // ---------------------------------------------------------------------------
  // Filter labels for the filter chips
  // ---------------------------------------------------------------------------
  const propertyLabels = useMemo<Record<string, string>>(() => {
    const labels: Record<string, string> = {};
    for (const [name, schema] of Object.entries(propertySchemas)) {
      labels[name] = schema.label || name;
    }
    return labels;
  }, [propertySchemas]);

  // `config.defaultFilterConditions` (e.g. the pipeline-scope condition
  // DealsList seeds from the route pipelineId) is documented on
  // useListState as producing a "visible, removable" chip — distinct from
  // user-added filters, which are surfaced only via the Filter button's
  // tooltip now that the separate chips bar is gone (see
  // FilterConditionsTooltip.tsx). Render just those default conditions as
  // dismissible chips next to the Filter button so they're actually visible
  // and clearable, matching the doc'd contract.
  const defaultConditionIds = useMemo(
    () =>
      new Set(
        (config.defaultFilterConditions ?? [])
          .map(c => c.id)
          .filter((id): id is string => !!id)
      ),
    [config.defaultFilterConditions]
  );

  // ---------------------------------------------------------------------------
  // Column helpers
  // ---------------------------------------------------------------------------
  const isInitialLoad = !currentOrg;
  const isDataLoading = isLoading || schemaLoading;

  // Count filters that are actually applied to the query (i.e. user clicked
  // "Apply filters"). The badge and the empty-state toggle both read from
  // this — reading from the in-flight `filterConditions` would flip-flop the
  // background as the user typed into the slideout.
  const validFilterCount = useMemo(() => {
    return listState.appliedFilterConditions.filter(condition => {
      const needsValue = !['is_empty', 'is_not_empty'].includes(
        condition.operator
      );
      if (!needsValue) {
        return true;
      }
      if (
        condition.value === null ||
        condition.value === undefined ||
        condition.value === ''
      ) {
        return false;
      }
      if (Array.isArray(condition.value) && condition.value.length === 0) {
        return false;
      }
      return true;
    }).length;
  }, [listState.appliedFilterConditions]);

  const hasActiveFilters = validFilterCount > 0;
  const hasActiveSearch = !!listState.filters.search;

  // For the empty-state variant, only count user-added filters (not
  // defaultFilterConditions). Default filters represent the baseline view —
  // when only defaults are active and no results match, the custom "no data"
  // message (e.g. "No booking links yet") is more helpful than the generic
  // "Nothing matches the current filters."
  const hasUserAddedFiltersOrSearch = useMemo(() => {
    if (hasActiveSearch) {
      return true;
    }
    const defaultIds = new Set(
      (config.defaultFilterConditions ?? [])
        .map(c => c.id)
        .filter((id): id is string => !!id)
    );
    if (defaultIds.size === 0) {
      return hasActiveFilters;
    }
    return listState.appliedFilterConditions.some(condition => {
      if (condition.id && defaultIds.has(condition.id)) {
        return false;
      }
      const needsValue = !['is_empty', 'is_not_empty'].includes(
        condition.operator
      );
      if (!needsValue) {
        return true;
      }
      if (
        condition.value === null ||
        condition.value === undefined ||
        condition.value === ''
      ) {
        return false;
      }
      if (Array.isArray(condition.value) && condition.value.length === 0) {
        return false;
      }
      return true;
    });
  }, [
    hasActiveSearch,
    hasActiveFilters,
    config.defaultFilterConditions,
    listState.appliedFilterConditions,
  ]);

  const hasListLoadError = !!listError && !hasUnsortableSortError;
  const listErrorBody = hasUserAddedFiltersOrSearch
    ? LIST_ERROR_BODY_FILTERED
    : LIST_ERROR_BODY_UNFILTERED;
  // Latest-value ref so the log/toast effect below can read the current copy
  // without depending on listErrorBody, which changes on filter/search edits
  // unrelated to the error itself (mirrors searchParamsRef above).
  const listErrorBodyRef = useRef(listErrorBody);
  listErrorBodyRef.current = listErrorBody;

  // Switch to Board mode while preserving existing query params (view, filters,
  // search, pagination, sort). Uses router navigation to avoid a full reload.
  // Writes `?mode=` — display mode is orthogonal to `?view=` (which view is
  // selected), so switching to the board must not disturb the active view.
  const handleSwitchToBoardView = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.set(MODE_PARAM, 'board');
    navigate({ search: `?${next.toString()}` });
  }, [navigate, searchParams]);

  // --- Header actions ---
  const headerActions = useMemo<HeaderAction[]>(() => {
    const acts: HeaderAction[] = [];
    if (config.stageAssociationTypeName) {
      acts.push({
        // visible when config.stageAssociationTypeName is set
        id: 'board-view',
        label: 'Board',
        onClick: handleSwitchToBoardView,
        icon: LayoutGrid01,
        variant: 'unstyled',
      });
    }
    // Page-specific extra actions (e.g. "View Bookings" on Booking Links page)
    if (extraHeaderActions) {
      acts.push(...extraHeaderActions);
    }
    // Show the Add button whenever a create flow is wired (onCreateObject or
    // onCreateClick); omitted entirely when both are undefined (system-created
    // types) OR when the user is a read-only viewer (no 'ahoy_default' group)
    // — viewers never see a write affordance, matching the residual-surface
    // treatment elsewhere (dashboards, imports, API keys). When the user has
    // 'ahoy_default' but lacks create permission for this specific entity
    // type we render it DISABLED with an explanatory tooltip rather than
    // hiding it — creation now authorizes as the user and fails closed
    // server-side, so an always-clickable button would lead straight to a 403.
    if (
      canWrite &&
      ((onCreateObject && genericCreateAllowed && genericTypeCreateAllowed) ||
        onCreateClick)
    ) {
      // The button disables for two independent reasons; the tooltip must
      // name the one that actually applies. A missing per-entity create
      // permission is a hard block and takes precedence with its permission
      // message. `createDisabled` is a transient, caller-driven unavailability
      // (e.g. a record's Tasks tab while the default stage resolves) — showing
      // a permission error there would mislead a user who DOES have permission,
      // so we surface the caller's reason (`createDisabledTooltip`) if any, or
      // no tooltip at all.
      let addDisabledTooltip: string | undefined;
      if (!canCreateObject) {
        addDisabledTooltip = `You don't have permission to create ${(
          config.pluralTitle || config.title
        ).toLowerCase()}`;
      } else if (createDisabled) {
        addDisabledTooltip = createDisabledTooltip;
      }
      acts.push({
        id: 'add',
        label: `Add ${config.title.toLowerCase()}`,
        onClick: onCreateClick ?? handleOpenCreateModal,
        variant: 'primary',
        isDisabled: !canCreateObject || createDisabled,
        disabledTooltip: addDisabledTooltip,
      });
    }
    return acts;
  }, [
    config.title,
    config.pluralTitle,
    config.stageAssociationTypeName,
    canCreateObject,
    createDisabled,
    createDisabledTooltip,
    canWrite,
    extraHeaderActions,
    genericCreateAllowed,
    genericTypeCreateAllowed,
    handleOpenCreateModal,
    handleSwitchToBoardView,
    onCreateObject,
    onCreateClick,
  ]);

  // ---------------------------------------------------------------------------
  // Totals row
  // ---------------------------------------------------------------------------
  const fieldTypeAggregations = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const ft of fieldTypes ?? []) {
      map[ft.field_type_name] = ft.aggregations;
    }
    return map;
  }, [fieldTypes]);

  const totalsRowStorageKey = `crm_aggregations_v1_${currentOrg?.id}_${config.entityType}`;

  const renderFooter = useCallback(
    (context: FooterRenderContext) => (
      <TotalsRow
        pane={context.pane}
        leftColumns={context.leftColumns}
        rightColumns={context.rightColumns}
        columnWidths={context.columnWidths}
        rightPaneContentWidth={context.rightPaneContentWidth}
        addColumnExtraWidth={context.addColumnExtraWidth}
        rowCount={total ?? 0}
        orgId={currentOrg?.id ?? 0}
        entityTypeName={config.entityType}
        storageKey={totalsRowStorageKey}
        schema={propertySchemas}
        fieldTypeAggregations={fieldTypeAggregations}
        filters={filtersWithSearch}
        convertTo={currentOrg?.defaultCurrency}
      />
    ),
    [
      total,
      currentOrg?.id,
      currentOrg?.defaultCurrency,
      config.entityType,
      totalsRowStorageKey,
      propertySchemas,
      fieldTypeAggregations,
      filtersWithSearch,
    ]
  );

  // ---------------------------------------------------------------------------
  // Stable prop callbacks (extracted to preserve React.memo on children)
  // ---------------------------------------------------------------------------
  // Destructure listState members so deps reference stable variables, not
  // property-access paths that trigger react-hooks/exhaustive-deps.
  const {
    setShowAdvancedFilters,
    showAdvancedFilters,
    filterConditionGroups,
    setFilterConditionGroups,
    appliedFilterConditionGroups,
    handleApplyAdvancedFilters,
    handleSortChange: listStateHandleSortChange,
    sortDescriptor: listSortDescriptor,
  } = listState;

  useEffect(() => {
    // A stored/replayed sort the backend now rejects (e.g. a saved view or a stale
    // pipeline-board descriptor naming an unsortable property) returns 400 keyed under
    // `sort`. Clear it so the list re-queries unsorted rather than showing an error/empty
    // table. Guard on an active descriptor so this fires once and cannot loop: clearing
    // sets the descriptor null (guard false) and the unsorted refetch returns 200.
    if (hasUnsortableSortError && listSortDescriptor != null) {
      listStateHandleSortChange(null);
    }
  }, [hasUnsortableSortError, listSortDescriptor, listStateHandleSortChange]);

  const handleRetryLoad = useCallback(() => {
    void refetch();
  }, [refetch]);

  // Log every list-load error once per error instance, and toast only when
  // previously-loaded rows are still on screen (placeholderData keeps `objects`
  // populated across an errored refetch) — an initial-load failure instead
  // renders the DataTable error empty state below.
  const handledListErrorRef = useRef<unknown>(null);
  useEffect(() => {
    if (!hasListLoadError) {
      handledListErrorRef.current = null;
      return;
    }
    if (handledListErrorRef.current === listError) {
      return;
    }
    handledListErrorRef.current = listError;
    const statusCode = isApiError(listError) ? listError.statusCode : undefined;
    const responseBody = isApiError(listError) ? listError.response : undefined;
    logger.error('CRM list query failed', listError ?? undefined, {
      entityType: config.entityType,
      statusCode,
      response: responseBody,
    });
    if (objects.length > 0) {
      toast.error(LIST_ERROR_HEADING, {
        description: listErrorBodyRef.current,
      });
    }
  }, [hasListLoadError, listError, objects.length, config.entityType]);

  const handleFilterClick = useCallback(
    () => setShowAdvancedFilters(!showAdvancedFilters),
    [setShowAdvancedFilters, showAdvancedFilters]
  );

  // Remove one default-seeded condition (by id) from both the working and
  // applied group state, then re-run the query — mirrors
  // handleApplyAdvancedFilters's own group-flattening, scoped to a single
  // condition instead of the whole slideout.
  const handleRemoveDefaultCondition = useCallback(
    (conditionId: string) => {
      const nextGroups = filterConditionGroups
        .map(group => ({
          ...group,
          conditions: group.conditions.filter(c => c.id !== conditionId),
        }))
        .filter(group => group.conditions.length > 0);
      setFilterConditionGroups(nextGroups);
      handleApplyAdvancedFilters(nextGroups);
    },
    [
      filterConditionGroups,
      setFilterConditionGroups,
      handleApplyAdvancedFilters,
    ]
  );

  const removableDefaultConditions = useMemo(
    () =>
      appliedFilterConditionGroups
        .flatMap(group => group.conditions)
        .filter(
          condition =>
            !!condition.id &&
            defaultConditionIds.has(condition.id) &&
            isFilterConditionValid(condition)
        ),
    [appliedFilterConditionGroups, defaultConditionIds]
  );

  const defaultFilterChips = useMemo(() => {
    // Wait for the org before rendering chips: EntityBadgeById can't hydrate
    // association values with orgId 0 (it would sit in loading forever).
    if (!currentOrg || removableDefaultConditions.length === 0) {
      return undefined;
    }
    return (
      <div className='flex items-center gap-2'>
        {removableDefaultConditions.map(condition => {
          const isAssociatedProperty = !!condition.target_property;
          const isAssociation =
            !!condition.association && !isAssociatedProperty;
          const label = getFilterConditionDisplayLabel(
            condition,
            associationLabelsByName,
            propertyLabels
          );
          return (
            <FilterConditionChip
              key={condition.id}
              condition={condition}
              label={label}
              propertyType={propertySchemas[condition.field]}
              isAssociation={isAssociation}
              orgId={currentOrg.id}
              onRemoveCondition={handleRemoveDefaultCondition}
            />
          );
        })}
      </div>
    );
  }, [
    removableDefaultConditions,
    associationLabelsByName,
    propertyLabels,
    propertySchemas,
    currentOrg,
    handleRemoveDefaultCondition,
  ]);

  const handleClearSelection = useCallback(() => {
    setSelectedKeys(new Set());
    setExcludedIds(new Set());
  }, [setSelectedKeys, setExcludedIds]);

  const handleSortChange = useCallback(
    (descriptor: Parameters<typeof listStateHandleSortChange>[0]) => {
      if (descriptor) {
        const mappedDescriptor =
          descriptor.column === 'object_name'
            ? { ...descriptor, column: 'ahoy_display_name' }
            : descriptor;
        listStateHandleSortChange(mappedDescriptor);
      } else {
        listStateHandleSortChange(null);
      }
    },
    [listStateHandleSortChange]
  );

  const handleRowClickCallback = useCallback(
    (object: string) => {
      const foundObject = objects.find(o => o.entity_id === object);
      if (foundObject) {
        handleRowClick(foundObject);
      }
    },
    [objects, handleRowClick]
  );

  const handleSlideoutOpenChange = useCallback(
    (isOpen: boolean) => setShowAdvancedFilters(isOpen),
    [setShowAdvancedFilters]
  );

  const handleSlideoutClose = useCallback(
    () => setShowAdvancedFilters(false),
    [setShowAdvancedFilters]
  );

  // Stable resolution of the table's "Create first" action. The DataTable's
  // empty state passes this as a prop; without memoization, an IIFE would
  // create a new function reference on every render of the list page and
  // invalidate any downstream memoization in DataTable.
  const onCreateFirst = useMemo(() => {
    // Fail closed: no "create first" action without create permission — creation
    // authorizes as the user server-side, so offering it would lead to a 403.
    // Also hidden entirely for viewers (canWrite false) — see headerActions.
    if (!canWrite || !canCreateObject) {
      return undefined;
    }
    if (onCreateClick) {
      return onCreateClick;
    }
    if (onCreateObject && genericCreateAllowed && genericTypeCreateAllowed) {
      return handleOpenCreateModal;
    }
    return undefined;
  }, [
    onCreateClick,
    onCreateObject,
    genericCreateAllowed,
    genericTypeCreateAllowed,
    canCreateObject,
    canWrite,
    handleOpenCreateModal,
  ]);

  const handleApplyAndClose = useCallback(() => {
    handleApplyAdvancedFilters();
    setShowAdvancedFilters(false);
  }, [handleApplyAdvancedFilters, setShowAdvancedFilters]);

  // Group-native auto-apply: the builder hands us the full groups on
  // condition/group removal, so apply them as-is (no flattening) to preserve
  // OR structure in both the applied snapshot and the backend query.
  const handleAutoApply = useCallback(
    (groups: FilterConditionGroup[]) => handleApplyAdvancedFilters(groups),
    [handleApplyAdvancedFilters]
  );

  const handleMergeClick = useCallback(
    (selected: CrmObject[]) => {
      if (selected.length !== 2) {
        toast.error('Select exactly 2 records to merge.');
        return;
      }
      const [a, b] = selected;
      if (
        !canMergeEntityTypes(
          a.entity_type_name,
          b.entity_type_name,
          entityKindsMap
        )
      ) {
        toast.error('These records cannot be merged.');
        return;
      }
      setMergeEntityIds([a.entity_id, b.entity_id]);
      setMergeWizardOpen(true);
    },
    [entityKindsMap]
  );

  const handleCloseBulkUpdate = useCallback(() => {
    setIsBulkUpdateOpen(false);
    setBulkUpdateSelectedObjects([]);
  }, []);

  const handleBulkUpdateAsyncDispatched = useCallback(
    (jobId: number, totalCount: number) => {
      if (!currentOrg || !user) {
        return;
      }
      setActiveBulkActionJobState({
        jobId,
        totalCount,
        entityTypeName: config.entityType,
      });
      setActiveBulkActionJob(
        user.id,
        currentOrg.id,
        jobId,
        totalCount,
        config.entityType
      );
    },
    [currentOrg, user, config.entityType]
  );

  const handleOpenBulkUpdate = useCallback(
    (selectedIds: string[]) => {
      if (isSelectAllMode) {
        setBulkUpdateSelectedObjects([]);
      } else {
        const selectedSet = new Set(selectedIds);
        setBulkUpdateSelectedObjects(
          objects.filter(o => selectedSet.has(o.entity_id))
        );
      }
      setIsBulkUpdateOpen(true);
    },
    [isSelectAllMode, objects]
  );

  // Adapt page-level BulkAction[] to BulkActionBar.BulkAction[] format.
  // Every action is gated by a declared `requires` (feature or entity
  // permission) — undeclared config actions fall back to the fail-closed
  // default (entity write permission), so a partial user sees exactly the
  // actions their grants allow. This replaces the old global `canWrite`
  // shortcut that gated Bulk update as an all-or-nothing toggle.
  const additionalBulkActions = useMemo(() => {
    const meetsRequirement = (requires: BulkActionRequirement | undefined) =>
      isBulkActionRequirementMet(
        requires ?? {
          permission: [config.entityType, 'ahoy_property_updated'],
        },
        { hasFeature, hasPermission }
      );

    const list: {
      key: string;
      label: string;
      icon?: React.FC<{ className?: string }>;
      destructive: boolean;
      onAction: (selectedIds: string[]) => void;
    }[] = [];

    // Bulk update keeps requiring entity write permission — now expressed as
    // a declaration like every other action instead of a separate ad-hoc
    // canWrite conditional.
    if (
      meetsRequirement({
        permission: [config.entityType, 'ahoy_property_updated'],
      })
    ) {
      list.push({
        key: 'bulk-update',
        label: 'Bulk update',
        icon: Edit03,
        destructive: false,
        onAction: handleOpenBulkUpdate,
      });
    }

    for (const action of config.bulkActions ?? []) {
      const requires =
        action.requires ?? KNOWN_BULK_ACTION_REQUIREMENTS[action.id];
      if (!meetsRequirement(requires)) {
        continue;
      }
      list.push({
        key: action.id,
        label: action.label,
        icon: action.icon as React.FC<{ className?: string }> | undefined,
        destructive: action.variant === 'danger',
        onAction: (selectedIds: string[]) => {
          if (isSelectAllMode && action.onClickWithFilters) {
            void action.onClickWithFilters(
              filtersWithSearch,
              Array.from(excludedIds).map(String),
              effectiveSelectedCount
            );
            return;
          }
          const selectedSet = new Set(selectedIds);
          const selectedObjects = objects.filter(o =>
            selectedSet.has(o.entity_id)
          );
          void action.onClick(selectedObjects);
        },
      });
    }

    // Inject merge action when:
    //  - user has permission to merge this entity type
    //  - NOT in select-all mode (merge is always exactly 2)
    //  - exactly 2 individually-selected records
    const showMerge =
      canMergeObject &&
      !isSelectAllMode &&
      listState.selectedKeys !== 'all' &&
      (listState.selectedKeys as Set<string | number>).size === 2;
    if (showMerge) {
      list.push({
        key: 'merge',
        label: 'Merge',
        icon: GitMerge,
        destructive: false,
        onAction: (selectedIds: string[]) => {
          const selectedSet = new Set(selectedIds);
          const selectedObjects = objects.filter(o =>
            selectedSet.has(o.entity_id)
          );
          handleMergeClick(selectedObjects);
        },
      });
    }
    return list;
  }, [
    hasFeature,
    hasPermission,
    config.entityType,
    handleOpenBulkUpdate,
    config.bulkActions,
    objects,
    isSelectAllMode,
    filtersWithSearch,
    excludedIds,
    effectiveSelectedCount,
    canMergeObject,
    listState.selectedKeys,
    handleMergeClick,
  ]);

  // Selection is only offered when at least one bulk action is available for
  // it — keyed on the resolved action list (never on seat/member kind), so
  // row checkboxes and the select-all header disappear together with the
  // last available action instead of leaving a selectable-but-actionless
  // table (the bar itself already no-ops when nothing is selected).
  const hasAnyBulkAction =
    (enableDelete && canDeleteObject) ||
    canExport ||
    additionalBulkActions.length > 0;

  // ---------------------------------------------------------------------------
  // Saved views — list surface adapter + hook
  // ---------------------------------------------------------------------------

  const svGetFilterConditionGroups = useCallback(
    (): FilterConditionGroup[] => appliedFilterConditionGroups,
    [appliedFilterConditionGroups]
  );

  const svApplyFilterConditionGroups = useCallback(
    (groups: FilterConditionGroup[]) => {
      // Seed the builder draft with the ORIGINAL groups (not a flattened
      // single-group shim) so a subsequently-opened builder shows the view's
      // real OR structure — flattening here would collapse OR→AND on the next
      // edit/apply/save loop. handleGroupsChange wraps setFilterConditionGroups.
      handleGroupsChange(groups);
      handleApplyAdvancedFilters(groups);
    },
    [handleGroupsChange, handleApplyAdvancedFilters]
  );

  const svCaptureViewState = useCallback((): SavedFilterViewState => {
    const columnVisibility: Record<string, boolean> = {};
    for (const id of hiddenColumnIds) {
      columnVisibility[id] = false;
    }
    // Fall back to the full (unfiltered) column set, not visibleColumns —
    // this feeds the debounced PATCH, so a hidden-filtered fallback here
    // would permanently bake the incomplete order into the saved view.
    const order = columnOrder ?? fullColumnOrder;
    return {
      version: 1,
      columnOrder: order,
      columnVisibility,
      sortDescriptor: listSortDescriptor,
    };
  }, [hiddenColumnIds, columnOrder, fullColumnOrder, listSortDescriptor]);

  const svApplyViewState = useCallback(
    (state: SavedFilterViewState) => {
      // Normalize sort the same way the live sort handler does
      // (object_name → ahoy_display_name).
      const savedSort = state.sortDescriptor;
      listStateHandleSortChange(
        savedSort && savedSort.column === 'object_name'
          ? { ...savedSort, column: 'ahoy_display_name' }
          : (savedSort ?? null)
      );

      // Replace dynamic columns with the saved view's column set.
      const configColumnIds = new Set(config.columns.map(getColumnKey));
      const savedIds = [
        ...(state.columnOrder ?? []),
        ...Object.keys(state.columnVisibility ?? {}),
      ];
      const columnsToRestore: ColumnConfig[] = [];
      const restoredIds = new Set<string>();
      for (const id of savedIds) {
        if (configColumnIds.has(id) || restoredIds.has(id)) {
          continue;
        }
        if (propertySchemas[id]) {
          columnsToRestore.push({ kind: 'property', name: id });
          restoredIds.add(id);
        } else if (
          associationTypes.some(at => at.association_type_name === id)
        ) {
          columnsToRestore.push({ kind: 'association', name: id });
          restoredIds.add(id);
        } else {
          // Lookup columns are compound ids (`<associationTypeName>.<targetPropertyName>`)
          // that never appear directly in propertySchemas or associationTypes.
          // Reconstruct by PARSING ALONE — deliberately NOT gated on the
          // association being loaded. This one-shot apply runs once when a view
          // is selected; if it dropped a lookup id whose association metadata
          // hadn't arrived yet, `validOrder` below would treat the id as unknown
          // and permanently remove it from the stored view. Parsing preserves the
          // column; if the association turns out to be genuinely gone once
          // loaded, the reactive `degradedLookupIds` memo render-excludes it while
          // the id stays in the saved columnOrder (see svCaptureViewState).
          const lookupParts = parseLookupColumnName(id);
          if (lookupParts) {
            const lookupCol: ColumnConfig = {
              kind: 'lookup',
              name: id,
              associationTypeName: lookupParts.associationTypeName,
              targetPropertyName: lookupParts.targetPropertyName,
            };
            columnsToRestore.push({
              ...lookupCol,
              sortable: isLookupColumnSortable(
                lookupCol,
                associationTypes,
                config.entityType,
                matchesSource,
                lookupTargetSchemasByType,
                objectTypesData?.allObjectTypes,
                isFieldTypeSortable
              ),
            });
            restoredIds.add(id);
          }
        }
      }
      setAddedColumns(columnsToRestore);

      const knownColumnIds = new Set([...configColumnIds, ...restoredIds]);
      const validOrder = (state.columnOrder ?? []).filter(id => {
        const known = knownColumnIds.has(id);
        if (!known) {
          logger.warn('Saved view references unknown column; skipping', {
            component: 'saved-views',
            column_id: id,
          });
        }
        return known;
      });
      setColumnOrder(validOrder.length > 0 ? validOrder : null);

      const nextHidden = new Set<string>();
      for (const [id, isVisible] of Object.entries(
        state.columnVisibility ?? {}
      )) {
        if (isVisible === false && knownColumnIds.has(id)) {
          nextHidden.add(id);
        }
      }
      setHiddenColumnIds(nextHidden);
    },
    [
      config.columns,
      config.entityType,
      propertySchemas,
      associationTypes,
      matchesSource,
      lookupTargetSchemasByType,
      objectTypesData?.allObjectTypes,
      isFieldTypeSortable,
      listStateHandleSortChange,
      setAddedColumns,
      setColumnOrder,
      setHiddenColumnIds,
    ]
  );

  const listSurface = useMemo<SavedViewSurface<SavedFilterViewState>>(
    () => ({
      targetEntityType: config.entityType,
      getFilterConditionGroups: svGetFilterConditionGroups,
      applyFilterConditionGroups: svApplyFilterConditionGroups,
      captureViewState: svCaptureViewState,
      applyViewState: svApplyViewState,
    }),
    [
      config.entityType,
      svGetFilterConditionGroups,
      svApplyFilterConditionGroups,
      svCaptureViewState,
      svApplyViewState,
    ]
  );

  const savedViewsOptions = useMemo(
    () => ({ getUrlFilterOverrides }),
    [getUrlFilterOverrides]
  );

  // Wire-shape inputs for useListUrlSync (declared here; the hook itself is
  // wired after useSavedViews, whose outputs it consumes).
  const urlAppliedFilterGroups = useMemo(
    () =>
      convertConditionGroupsToFilterGroups(appliedFilterConditionGroups)
        .filter_groups,
    [appliedFilterConditionGroups]
  );
  // The clean-URL filter baseline: the page's visible default chips. Forced
  // defaults (defaultFilters.filter_groups) and built-in baselines are hidden
  // and never appear in appliedFilterConditionGroups, so they are not part of
  // this baseline either.
  const urlDefaultFilterGroups = useMemo(
    () =>
      config.defaultFilterConditions?.length
        ? convertConditionGroupsToFilterGroups([
            { id: 'group-0', conditions: config.defaultFilterConditions },
          ]).filter_groups
        : [],
    [config.defaultFilterConditions]
  );
  const urlDefaultSortDescriptor = useMemo(
    () =>
      config.defaultFilters?.sort
        ? {
            column: config.defaultFilters.sort,
            direction:
              config.defaultFilters.order === 'asc'
                ? ('ascending' as const)
                : ('descending' as const),
          }
        : null,
    [config.defaultFilters?.sort, config.defaultFilters?.order]
  );

  const {
    activeViewId,
    activeSavedView,
    applyView,
    filtersDirtyState,
    viewDirtyState,
    activeSavedViewFilterGroups,
    canWriteActiveView,
    saveViewChanges,
    isSavingViewChanges,
    discardViewChanges,
    isApplyingView,
    surfaceTransitionPending,
    selectAll: handleSelectAllView,
    resetSurface,
    cancelPendingApply,
    saveModalOpen: isSaveModalOpen,
    savePayload,
    openSaveModal: handleOpenSaveModal,
    closeSaveModal,
    onViewSaved: handleViewSaved,
    onViewDeleted: handleViewDeleted,
  } = useSavedViews(listSurface, activeListView, savedViewsOptions);

  // ---------------------------------------------------------------------------
  // URL sync — mirrors applied filters / sort / search into ?filters= /
  // ?sort= / ?search= and reflects back/forward URL changes into state.
  // Wired AFTER useSavedViews (it consumes the dirty tri-state) and reading
  // ONLY appliedFilterConditionGroups (never filters.filter_groups /
  // filtersWithSearch, which carry forced defaults / built-in baselines that
  // must not leak into the URL).
  // ---------------------------------------------------------------------------
  const urlApplyFilterGroups = useCallback(
    (groups: FilterGroup[]) => {
      const conditionGroups = filterGroupsToConditionGroups(groups);
      handleGroupsChange(conditionGroups);
      handleApplyAdvancedFilters(conditionGroups);
    },
    [handleGroupsChange, handleApplyAdvancedFilters]
  );

  // The saved view's stored sort, normalized the same way svApplyViewState
  // normalizes it on apply (object_name → ahoy_display_name) — without this a
  // freshly-applied view would read as a sort divergence and spawn a spurious
  // ?sort= param.
  const urlActiveSavedViewSortDescriptor = useMemo(() => {
    const stored = activeSavedView?.view_state.sortDescriptor ?? null;
    return stored && stored.column === 'object_name'
      ? { ...stored, column: 'ahoy_display_name' }
      : stored;
  }, [activeSavedView?.view_state.sortDescriptor]);

  useListUrlSync({
    appliedFilterGroups: urlAppliedFilterGroups,
    sortDescriptor: listSortDescriptor,
    searchText: listState.filters.search ?? '',
    defaultFilterGroups: urlDefaultFilterGroups,
    defaultSortDescriptor: urlDefaultSortDescriptor,
    activeSavedViewId: activeViewId,
    filtersDirtyState,
    activeSavedViewFilterGroups,
    activeSavedViewSortDescriptor: urlActiveSavedViewSortDescriptor,
    surfaceTransitionPending,
    onApplyFilterGroups: urlApplyFilterGroups,
    onApplySort: handleSortChange,
    onApplySearch: listState.handleSearch,
  });

  // Unified view selection. Selecting a saved view applies it asynchronously —
  // applyView commits the identity + URL on success and leaves the current view
  // (and its baseline) untouched on failure. Selecting a built-in view sets the
  // identity directly; the surface is reset only when leaving a saved view, so
  // switching between built-ins preserves ad-hoc filters/columns.
  const handleSelectSavedView = useCallback(
    (viewId: string) => {
      void applyView(viewId);
    },
    [applyView]
  );
  const handleSelectBuiltInView = useCallback(
    (viewId: string) => {
      // Always invalidate a pending saved-view apply so a late commit cannot
      // clobber this built-in selection. Leaving a saved view additionally
      // resets the surface (which also cancels); switching between built-ins
      // preserves ad-hoc filters/columns, so only cancel the pending apply.
      if (activeView?.kind === 'saved') {
        resetSurface();
      } else {
        cancelPendingApply();
      }
      setActiveView({ kind: 'builtin', id: viewId });
    },
    [activeView, resetSurface, cancelPendingApply, setActiveView]
  );

  const handleSaveModalOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        closeSaveModal();
      }
    },
    [closeSaveModal]
  );

  const handleSetSavedViewAsDefault = useCallback(
    (view: SavedFilterView) =>
      handleSetDefaultView({ kind: 'saved', id: view.entity_id }),
    [handleSetDefaultView]
  );

  // SavedViewTabs stays presentational: it renders built-in + saved pills and is
  // told which of each is active. Both "active" inputs are derived from the ONE
  // unified activeView identity, so there is a single source of truth underneath.
  // Built all-or-nothing so it matches SavedViewTabsProps' discriminated union —
  // a partial spread of config.builtInViews would widen back to optional
  // built-in-view props and fail the type check.
  const activeBuiltInViewId =
    activeView?.kind === 'builtin' ? activeView.id : null;
  const savedViewTabsProps: SavedViewTabsProps = config.builtInViews
    ? {
        orgId: currentOrg?.id ?? 0,
        entityType: config.entityType,
        activeViewId,
        onSelectView: handleSelectSavedView,
        onSelectAll: handleSelectAllView,
        onSaveCurrentView: handleOpenSaveModal,
        onViewDeleted: handleViewDeleted,
        viewDirty: viewDirtyState === 'dirty',
        canSaveActiveView: canWriteActiveView,
        onSaveActiveViewChanges: saveViewChanges,
        isSavingViewChanges,
        onDiscardViewChanges: discardViewChanges,
        builtInViews: config.builtInViews,
        activeBuiltInViewId,
        onSelectBuiltInView: handleSelectBuiltInView,
        defaultViewRef,
        onSetDefaultView: handleSetDefaultView,
        onClearDefaultView: handleClearDefaultView,
        canCreateView: canWrite,
      }
    : {
        orgId: currentOrg?.id ?? 0,
        entityType: config.entityType,
        activeViewId,
        onSelectView: handleSelectSavedView,
        onSelectAll: handleSelectAllView,
        onSaveCurrentView: handleOpenSaveModal,
        onViewDeleted: handleViewDeleted,
        defaultViewRef,
        onSetDefaultView: handleSetDefaultView,
        onClearDefaultView: handleClearDefaultView,
        viewDirty: viewDirtyState === 'dirty',
        canSaveActiveView: canWriteActiveView,
        onSaveActiveViewChanges: saveViewChanges,
        isSavingViewChanges,
        onDiscardViewChanges: discardViewChanges,
        canCreateView: canWrite,
      };

  let resolvedEmptyStateVariant: 'no-data' | 'no-results' | 'error';
  if (hasListLoadError && objects.length === 0) {
    resolvedEmptyStateVariant = 'error';
  } else if (hasUserAddedFiltersOrSearch) {
    resolvedEmptyStateVariant = 'no-results';
  } else {
    resolvedEmptyStateVariant = 'no-data';
  }

  // The no-data empty state's default copy ("Get started by creating your
  // first X") invites an action a viewer can't take. Swap in neutral copy
  // for read-only users unless the config already supplies its own
  // (write-capable users keep the config default / create-oriented copy).
  const emptyStateDescription = canWrite
    ? config.emptyState?.description
    : (config.emptyState?.description ??
      `${config.pluralTitle || `${config.title}s`} will appear here once added.`);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <CrmListLayout
      entityTitle={config.pluralTitle || config.title}
      entityIcon={getEntityTypeIconComponent(config.entityType)}
      recordCount={total ?? 0}
      searchValue={listState.filters.search || ''}
      onSearch={listState.handleSearch}
      searchPlaceholder={`Search ${(config.pluralTitle || config.title).toLowerCase()}`}
      hasActiveFilters={hasActiveFilters}
      onFilterClick={handleFilterClick}
      filterConditions={listState.appliedFilterConditions}
      filterConditionGroups={appliedFilterConditionGroups}
      propertyLabels={propertyLabels}
      propertySchemas={propertySchemas}
      associationLabels={associationLabelsByName}
      orgId={currentOrg?.id ?? 0}
      actions={headerActions}
      toolbarLeading={toolbarLeading}
      toolbarTrailing={toolbarTrailing}
      countLabel={countLabel}
      countLabelSingular={countLabelSingular}
      toolbarExtra={defaultFilterChips}
      embedded={embedded}
      titleTrailing={
        currentOrg && user ? (
          <SavedViewTabs {...savedViewTabsProps} />
        ) : undefined
      }
    >
      {/* Bulk action bar (portaled to body, fixed at bottom center) */}
      <BulkActionBar
        selectedIds={selectedIdsArray}
        onClearSelection={handleClearSelection}
        onDelete={
          enableDelete && canDeleteObject ? handleBulkDelete : undefined
        }
        onExport={canExport ? handleExport : undefined}
        isExporting={isExporting}
        additionalActions={additionalBulkActions}
        isAllSelected={isSelectAllMode}
        totalCount={effectiveSelectedCount}
      />

      {/* Table */}
      <div className='flex-1 min-h-0 h-full'>
        <DataTable
          columns={tableColumns}
          rows={tableRows}
          pinnedColumnIds={effectivePinnedColumnIds}
          selectionMode={hasAnyBulkAction ? 'multiple' : 'none'}
          selection={listState.selectedKeys}
          excludedKeys={listState.excludedIds as Set<string>}
          onSelectionChange={handleSelectionChange}
          sortDescriptor={listState.sortDescriptor ?? null}
          onSortChange={handleSortChange}
          isLoading={
            isInitialLoad ||
            isDataLoading ||
            isApplyingView ||
            isDefaultResolving
          }
          getCellEditConfig={getCellEditConfig}
          onRowClick={handleRowClickCallback}
          storageKey={`table_${currentOrg?.id}_${config.entityType}`}
          aria-label={`${config.pluralTitle} table`}
          entityType={config.title.toLowerCase()}
          emptyStateVariant={resolvedEmptyStateVariant}
          errorHeading={LIST_ERROR_HEADING}
          errorMessage={listErrorBody}
          onRetry={handleRetryLoad}
          emptyTitle={config.emptyState?.heading}
          emptyDescription={emptyStateDescription}
          onCreateFirst={onCreateFirst}
          createFirstDisabled={createDisabled}
          onClearFilters={undefined}
          totalRowCount={total ?? undefined}
          onLoadRows={handleLoadRows}
          // Add Column feature
          availableColumns={availableColumns}
          onAddColumn={handleAddColumn}
          onCreateAttribute={
            canCreateAttribute ? handleCreateAttribute : undefined
          }
          // Column Header Menu feature
          onColumnReorder={handleColumnReorder}
          columnOrderControlled={activeViewId !== null}
          onColumnHide={handleColumnHide}
          renderFooter={renderFooter}
          // Row context actions (three-dot menu) - rendered inline in Name column
          getRowContextActions={getRowContextActions}
          inlineRowActions
        />
      </div>

      {/* Filter slideout */}
      {currentOrg && (
        <SlideoutMenu
          isOpen={listState.showAdvancedFilters}
          onOpenChange={handleSlideoutOpenChange}
        >
          <SlideoutMenu.Header onClose={handleSlideoutClose}>
            <h2 className='text-lg font-semibold text-primary'>
              Filter {config.pluralTitle || config.title}
            </h2>
          </SlideoutMenu.Header>
          <FormProvider fields={filterFieldConfig} values={filterFormValues}>
            <SlideoutMenu.Content>
              <SchemaFilterBuilder
                orgId={currentOrg.id}
                entityType={config.entityType}
                groups={filterGroups}
                onChange={handleGroupsChange}
                allowGroups={true}
                onApply={handleApplyAndClose}
                onAutoApply={handleAutoApply}
                onValidityChange={setFiltersAreValid}
                enableAssociatedPropertyFilters
              />
            </SlideoutMenu.Content>
            <SlideoutMenu.Footer className='flex w-full items-center justify-between gap-3'>
              <Button
                size='md'
                color='tertiary'
                onClick={listState.handleResetFilters}
              >
                Reset filters
              </Button>
              <SubmitButton
                size='md'
                color='primary'
                onClick={handleApplyAndClose}
              >
                Apply filters
              </SubmitButton>
            </SlideoutMenu.Footer>
          </FormProvider>
        </SlideoutMenu>
      )}

      {/* Unsaved changes dialog */}
      <UnsavedChangesModal {...unsavedModalProps} />

      {/* Delete confirmation modal */}
      {deleteConfirmation && (
        <DeleteCRMObjectModal
          isOpen={deleteConfirmation.isOpen}
          onOpenChange={handleDeleteModalOpenChange}
          entityTypeLabel={config.title}
          objectName={deleteConfirmation.objectName}
          title={deleteConfirmation.title}
          description={deleteConfirmation.description}
          onConfirm={deleteConfirmation.onConfirm}
          isDeleting={isDeleting}
        />
      )}
      {activeBulkActionJob && currentOrg ? (
        <BulkActionBanner
          key={activeBulkActionJob.jobId}
          orgId={currentOrg.id}
          activeJobId={activeBulkActionJob.jobId}
          totalCount={activeBulkActionJob.totalCount}
          entityTypeName={activeBulkActionJob.entityTypeName}
          onDismiss={handleBannerDismiss}
        />
      ) : null}

      {currentOrg && savePayload && (
        <SaveCurrentViewModal
          isOpen={isSaveModalOpen}
          onOpenChange={handleSaveModalOpenChange}
          orgId={currentOrg.id}
          entityType={config.entityType}
          orgName={currentOrg.name}
          payload={savePayload}
          onSaved={handleViewSaved}
          onSetAsDefault={handleSetSavedViewAsDefault}
        />
      )}

      {mergeWizardOpen && mergeEntityIds && currentOrg && (
        <MergeWizard
          isOpen={mergeWizardOpen}
          onOpenChange={setMergeWizardOpen}
          orgId={currentOrg.id}
          entityTypeName={config.entityType}
          entityIds={mergeEntityIds}
          onMergeComplete={handleClearSelection}
        />
      )}

      {currentOrg?.id && (
        <BulkUpdateModal
          isOpen={isBulkUpdateOpen}
          orgId={currentOrg.id}
          entityTypeName={config.entityType}
          selectedObjects={bulkUpdateSelectedObjects}
          isSelectAllMode={isSelectAllMode}
          totalCount={effectiveSelectedCount}
          filterGroups={filtersWithSearch.filter_groups}
          excludedEntityIds={
            isSelectAllMode ? Array.from(excludedIds).map(String) : undefined
          }
          search={filtersWithSearch.search}
          searchProperties={filtersWithSearch.search_properties}
          onClose={handleCloseBulkUpdate}
          onAsyncDispatched={handleBulkUpdateAsyncDispatched}
          onSyncComplete={refetch}
        />
      )}
    </CrmListLayout>
  );
}
